import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  Zap, Mail, Download, RefreshCw, Loader2, Search, Filter,
  CheckCircle, AlertCircle, Clock, Eye,
  Sparkles, FileText, FolderOpen, Settings, XCircle,
  Paperclip, Calendar, User,
  Pause, Play, Trash2, Plus, Edit2, ToggleLeft, ToggleRight,
  List, FileSearch, Save, RotateCcw, AlertTriangle, Send, BarChart3,
  SkipForward, Check, Undo2, ExternalLink,
  ChevronUp, ChevronDown
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter
} from '../components/ui/dialog';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../components/ui/select';
import { automatizacionAPI, atencionClienteAPI, expedientesAPI, adjuntosAPI } from '../api';
import axios from 'axios';
import EstadisticasAutomatizacion from '../components/EstadisticasAutomatizacion';
import EmailsClientes from '../components/EmailsClientes';
import EmailsAbogados from '../components/EmailsAbogados';
import PanelConsumoIA from '../components/PanelConsumoIA';
import DialogEstimacionLote from '../components/DialogEstimacionLote';
import DialogEscritoIA from '../components/DialogEscritoIA';
import { toast } from '../hooks/use-toast';
import { useAuth } from '../contexts/AuthContext';
import RichTextEditor from '../components/RichTextEditor';

const ESTADOS = {
  nuevo: { label: 'Nuevo', color: 'bg-gray-100 text-gray-700', icon: Mail },
  descargado: { label: 'Descargado', color: 'bg-blue-100 text-blue-700', icon: Download },
  procesando_ia: { label: 'Procesando IA', color: 'bg-purple-100 text-purple-700', icon: Sparkles },
  pendiente_revision: { label: 'Pendiente Revisión', color: 'bg-amber-100 text-amber-700', icon: Clock },
  confirmado: { label: 'Confirmado', color: 'bg-green-100 text-green-700', icon: CheckCircle },
  error: { label: 'Error', color: 'bg-red-100 text-red-700', icon: AlertCircle },
  descartado: { label: 'Descartado', color: 'bg-gray-100 text-gray-500', icon: XCircle },
};

const TIPOS_TRAMITE = [
  { value: 'notificacion', label: 'Notificacion' },
  { value: 'escrito', label: 'Escrito' },
  { value: 'respuesta', label: 'Respuesta' },
  { value: 'tramite', label: 'Tramite' },
  { value: 'providencia', label: 'Providencia' },
  { value: 'sentencia', label: 'Sentencia' },
  { value: 'auto', label: 'Auto' },
  { value: 'emplazamiento', label: 'Emplazamiento' },
  { value: 'tasacion_costas', label: 'Tasacion de Costas' },
  { value: 'recurso_apelacion_banco', label: "Recurs apel·lacio (part contraria)" },
  { value: 'senalamiento', label: "Senyalament (vista/audiencia/judici)" },
  { value: 'decreto_costas', label: 'Decret aprovació de costes' },
  { value: 'decreto_intereses', label: "Decret aprovació d'interessos" },
  { value: 'decreto_costas_intereses', label: "Decret aprovació de costes + interessos" },
];

// Import en format espanyol (1.234,56) sense dependre de l'ICU del navegador
const fmtImportDecreto = (v) => {
  const [ent, dec] = Number(v || 0).toFixed(2).split('.');
  return `${ent.replace(/\B(?=(\d{3})+(?!\d))/g, '.')},${dec}`;
};

export default function Automatizacion() {
  const { user } = useAuth();
  const [mainSection, setMainSection] = useState('procuradores');
  const [activeTab, setActiveTab] = useState('emails');

  // Helper para verificar sub-permisos de automatizacion
  const hasSubPermiso = (subId, tipo = 'lectura') => {
    if (!user?.permisos) return true;
    if (user.rol === 'admin') return true;
    const p = user.permisos[subId];
    if (!p) return false;
    if (typeof p === 'boolean') return p;
    return !!p[tipo];
  };

  const canEmails = hasSubPermiso('automatizacion-emails');
  const canEstadisticas = hasSubPermiso('automatizacion-estadisticas');
  const canConfig = hasSubPermiso('automatizacion-config');
  // Tab inicial segun permisos
  const defaultTab = canEmails ? 'emails' : canEstadisticas ? 'estadisticas' : canConfig ? 'reglas' : 'emails';

  // Permisos seccion clientes
  const canClientesAny = hasSubPermiso('automatizacion-clientes-emails') || hasSubPermiso('automatizacion-clientes-estadisticas') || hasSubPermiso('automatizacion-clientes-config');
  const canProcuradoresAny = canEmails || canEstadisticas || canConfig;

  const [emails, setEmails] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(false);
  const [fetching, setFetching] = useState(false);
  const [processingAll, setProcessingAll] = useState(false);
  const [processAllJob, setProcessAllJob] = useState(null); // { job_id, total, procesados, exitosos, errores, estado, email_actual }
  const [selectedEmails, setSelectedEmails] = useState(new Set()); // IDs seleccionados para procesar
  const [discardConfirm, setDiscardConfirm] = useState(null); // email id to discard
  const [searchQuery, setSearchQuery] = useState('');
  const [filterEstado, setFilterEstado] = useState('descargado');
  const [emailSubTab, setEmailSubTab] = useState('sin_procesar');
  const [currentPage, setCurrentPage] = useState(1);
  const [totalEmails, setTotalEmails] = useState(0);
  const PAGE_SIZE = 150;

  // Detail dialog
  const [detailDialog, setDetailDialog] = useState({ open: false, email: null });
  const [escritoIADialog, setEscritoIADialog] = useState({ open: false, email: null });
  const [detailLoading, setDetailLoading] = useState(false);
  const [processingIA, setProcessingIA] = useState(null);

  // Confirm dialog
  const [confirmDialog, setConfirmDialog] = useState({ open: false, email: null });
  const [previewPdfIdx, setPreviewPdfIdx] = useState(0);
  const [previewPdfUrl, setPreviewPdfUrl] = useState('');
  const [confirmForm, setConfirmForm] = useState({
    expediente_id: '', titulo: '', resumen: '', tipo_tramite: 'tramite',
    tiene_plazo: false, fecha_limite: '', es_urgente: false,
    notificar_cliente: true,
  });
  const [confirming, setConfirming] = useState(false);
  const [savingDraft, setSavingDraft] = useState(false);
  const [reanalyzing, setReanalyzing] = useState(false);
  const [redownloading, setRedownloading] = useState(false);

  // Expediente search
  const [expSearch, setExpSearch] = useState('');
  const [expResults, setExpResults] = useState([]);
  const [selectedExp, setSelectedExp] = useState(null);

  // v38: Tab "Actuaciones" en panel derecho del confirm dialog
  const [rightPanelTab, setRightPanelTab] = useState('avisos');
  const [actuacionesPrevias, setActuacionesPrevias] = useState([]);
  const [loadingActuaciones, setLoadingActuaciones] = useState(false);
  const [expandedActuacionId, setExpandedActuacionId] = useState(null);
  const [adjuntosPorActuacion, setAdjuntosPorActuacion] = useState({});  // {actuacionId: [adjuntos]}
  const [actuacionPreview, setActuacionPreview] = useState({ adjuntoId: null, url: '', nombre: '' });

  // Config dialog
  const [configDialog, setConfigDialog] = useState(false);

  // Email Composer dialog (post-confirmation for Sentencias)
  const [emailComposer, setEmailComposer] = useState({
    open: false,
    consulta_id: null,
    destinatario_email: '',
    destinatario_nombre: '',
    asunto: '',
    cuerpo: '',
    expediente_numero: '',
  });
  const [emailSending, setEmailSending] = useState(false);
  const [emailPlantillas, setEmailPlantillas] = useState([]);

  // Post-confirmation step flow (replicates NuevaActuacionIA workflow)
  const [postConfirmStep, setPostConfirmStep] = useState('idle');
  // 'idle' | 'email_sentencia' | 'datos_faltantes'
  const [generatingEmail, setGeneratingEmail] = useState(false);
  const [emailSentenciaData, setEmailSentenciaData] = useState(null);
  const [emailSentenciaAsunto, setEmailSentenciaAsunto] = useState('');
  const [emailSentenciaCuerpo, setEmailSentenciaCuerpo] = useState('');
  const [emailSentenciaDestinatario, setEmailSentenciaDestinatario] = useState('');
  const [sendingEmailSentencia, setSendingEmailSentencia] = useState(false);
  const [decretoInfo, setDecretoInfo] = useState(null);
  const [decretoIdioma, setDecretoIdioma] = useState('ca');
  const [cambiandoIdioma, setCambiandoIdioma] = useState(false);
  const pendingDatosFaltantesRef = useRef([]);
  const postConfirmExpIdRef = useRef(null);
  const postConfirmAdjuntosRef = useRef({ emailAdjuntos: [], emailId: null });

  // Datos faltantes del expediente
  const [datosFaltantes, setDatosFaltantes] = useState([]);
  const [datosSeleccionados, setDatosSeleccionados] = useState({});
  // __V62A__ Bulk confirm — noms amb prefix 'bulk' per evitar col·lisions
  const [bulkSelectedEmails, setBulkSelectedEmails] = useState(new Set());
  const [bulkConfirmLoading, setBulkConfirmLoading] = useState(false);
  const [bulkConfirmDialog, setBulkConfirmDialog] = useState(false);
  const [bulkConfirmResult, setBulkConfirmResult] = useState(null);
  const [bulkMinConfianza, setBulkMinConfianza] = useState(0);
  const [bulkNotificarCliente, setBulkNotificarCliente] = useState(true);
  const [actualizandoDatos, setActualizandoDatos] = useState(false);

  // Fetch dialog
  const [fetchDialog, setFetchDialog] = useState(false);
  const [fetchForm, setFetchForm] = useState({
    // Por defecto: ultimos 30 dias (fecha LOCAL, sin desfase UTC) y SIN tope
    // superior, para no perder correos de dias anteriores. Con el backend
    // eficiente (Pieza A) el 'limit' es el maximo de NUEVOS por descarga.
    fecha_desde: new Date(Date.now() - 30 * 86400000 - new Date().getTimezoneOffset() * 60000).toISOString().split('T')[0],
    fecha_hasta: '',
    hora_desde: '',
    hora_hasta: '',
    remitente: '',
    limit: 200,
  });
  const [config, setConfig] = useState(null);
  const [configForm, setConfigForm] = useState({
    nombre: 'Procuradores',
    imap_host: 'mail.lexduo.cat',
    imap_port: 993,
    imap_user: '',
    imap_password: '',
    imap_use_ssl: true,
    imap_carpeta: 'INBOX',
    auto_fetch: false,
    auto_fetch_intervalo_min: 15,
    auto_process_ia: true,
  });
  const [savingConfig, setSavingConfig] = useState(false);
  const [testingConnection, setTestingConnection] = useState(false);

  // Map sub-tabs to filter estado
  const SUB_TAB_FILTERS = {
    sin_procesar: 'descargado',
    pendientes: 'pendiente_revision',
    borradores: 'borrador',
    urgentes: 'urgente',
    confirmados: 'confirmado',
    errores: 'error',
    descartados: 'descartado',
    archivo: '',
  };

  const handleSubTabChange = (tab) => {
    setEmailSubTab(tab);
    setCurrentPage(1);
    if (tab === 'urgentes') {
      setFilterEstado('pendiente_revision');
    } else if (tab === 'borradores') {
      setFilterEstado('pendiente_revision');
    } else if (tab === 'sin_procesar') {
      setFilterEstado('descargado');
    } else if (tab === 'descartados') {
      setFilterEstado('descartado');
    } else {
      setFilterEstado(SUB_TAB_FILTERS[tab] || '');
    }
  };

  // __V62A__ Bulk confirm helpers
  const bulkToggleEmail = (id) => {
    setBulkSelectedEmails(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };
  const bulkSelectAllVisible = (allIds) => setBulkSelectedEmails(new Set(allIds));
  const bulkClearSelection = () => setBulkSelectedEmails(new Set());
  const runBulkConfirm = async (mode) => {
    setBulkConfirmLoading(true);
    setBulkConfirmResult(null);
    try {
      const body = mode === 'all'
        ? { all_pending: true, min_confianza: bulkMinConfianza || null, max_emails: 200, notificar_cliente: bulkNotificarCliente }
        : { email_ids: Array.from(bulkSelectedEmails), min_confianza: null, notificar_cliente: bulkNotificarCliente };
      const tok = localStorage.getItem('lexduo_token');
      const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/automatizacion/emails-procuradores/bulk-confirm-and-notify`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...(tok ? { Authorization: `Bearer ${tok}` } : {}) },
        body: JSON.stringify(body),
      });
      const json = await res.json();
      if (!res.ok) throw new Error(json?.detail || 'Error bulk-confirm');
      setBulkConfirmResult(json);
      bulkClearSelection();
      await loadEmails();
      try { await loadStats(); } catch(_) {}
    } catch (e) {
      setBulkConfirmResult({ error: e.message || 'Error desconegut' });
    } finally {
      setBulkConfirmLoading(false);
    }
  };

  const loadEmails = useCallback(async () => {
    setLoading(true);
    try {
      const params = { limit: PAGE_SIZE, offset: (currentPage - 1) * PAGE_SIZE };
      if (emailSubTab === 'urgentes') {
        params.estado = 'pendiente_revision';
        params.urgente = true;
      } else if (emailSubTab === 'borradores') {
        params.estado = 'pendiente_revision';
        params.borrador = true;
      } else if (emailSubTab === 'sin_procesar') {
        params.estado = 'descargado';
      } else if (emailSubTab === 'descartados') {
        params.estado = 'descartado';
      } else if (filterEstado) {
        params.estado = filterEstado;
      }
      if (searchQuery) params.buscar = searchQuery;
      const res = await automatizacionAPI.listEmails(params);
      setEmails(res.data.emails || []);
      setTotalEmails(res.data.total || 0);
    } catch (err) {
      console.error('Error cargando emails:', err);
    } finally {
      setLoading(false);
    }
  }, [filterEstado, searchQuery, currentPage, emailSubTab]);

  const loadStats = useCallback(async () => {
    try {
      const res = await automatizacionAPI.getStats();
      setStats(res.data);
    } catch (err) {
      console.error('Error cargando stats:', err);
    }
  }, []);

  const loadConfig = useCallback(async () => {
    try {
      const res = await automatizacionAPI.getConfig();
      if (res.data.config) {
        setConfig(res.data.config);
        setConfigForm({
          nombre: res.data.config.nombre || 'Procuradores',
          imap_host: res.data.config.imap_host || 'mail.lexduo.cat',
          imap_port: res.data.config.imap_port || 993,
          imap_user: res.data.config.imap_user || '',
          imap_password: '',
          imap_use_ssl: res.data.config.imap_use_ssl !== false,
          imap_carpeta: res.data.config.imap_carpeta || 'INBOX',
          auto_fetch: res.data.config.auto_fetch || false,
          auto_fetch_intervalo_min: res.data.config.auto_fetch_intervalo_min || 15,
          auto_process_ia: res.data.config.auto_process_ia !== false,
        });
      }
    } catch (err) {
      console.error('Error cargando config:', err);
    }
  }, []);

  useEffect(() => {
    loadEmails();
    loadStats();
    loadConfig();
  }, [loadEmails, loadStats, loadConfig]);

  // Fetch new emails
  const handleFetchEmails = async () => {
    if (!config) {
      toast({ title: 'Configure primero la cuenta de email', variant: 'destructive' });
      setConfigDialog(true);
      return;
    }
    if (!config.activo) {
      toast({ title: 'La automatización está pausada', description: 'Reanude la automatización para descargar emails', variant: 'destructive' });
      return;
    }
    setFetchDialog(true);
  };

  const executeFetch = async () => {
    setFetching(true);
    setFetchDialog(false);
    try {
      const body = {
        limit: fetchForm.limit || 50,
      };
      if (fetchForm.fecha_desde) body.fecha_desde = fetchForm.fecha_desde;
      if (fetchForm.fecha_hasta) body.fecha_hasta = fetchForm.fecha_hasta;
      if (fetchForm.hora_desde) body.hora_desde = fetchForm.hora_desde;
      if (fetchForm.hora_hasta) body.hora_hasta = fetchForm.hora_hasta;
      if (fetchForm.remitente) body.remitente = fetchForm.remitente;

      const res = await automatizacionAPI.fetchEmails(body);
      const d = res.data;
      let desc = `${d.nuevos} nuevos, ${d.duplicados} duplicados`;
      if (d.filtrados_hora > 0) desc += `, ${d.filtrados_hora} fuera del horario`;
      desc += ` (${d.total_en_servidor} en servidor)`;
      toast({ title: 'Descarga completada', description: desc });
      loadEmails();
      loadStats();
    } catch (err) {
      toast({ title: 'Error', description: err.response?.data?.detail || 'Error descargando emails', variant: 'destructive' });
    } finally {
      setFetching(false);
    }
  };

  // Toggle pause/resume
  const handleToggleActive = async () => {
    try {
      const res = await automatizacionAPI.toggleActive();
      toast({ title: res.data.message });
      loadConfig();
    } catch (err) {
      toast({ title: 'Error', description: err.response?.data?.detail || 'Error cambiando estado', variant: 'destructive' });
    }
  };

  // Delete permanently
  const handleDelete = async (emailId) => {
    try {
      await automatizacionAPI.deleteEmail(emailId);
      toast({ title: 'Email eliminado definitivamente' });
      loadEmails();
      loadStats();
      setDetailDialog({ open: false, email: null });
    } catch (err) {
      toast({ title: 'Error', description: 'Error eliminando email', variant: 'destructive' });
    }
  };

  // Process all with IA (background)
  const [estimacionLoteOpen, setEstimacionLoteOpen] = useState(false);
  const [iaConfigActual, setIaConfigActual] = useState(null);

  const handleClickProcessAll = async () => {
    // Pre-chequeo: cargar config IA actual y mostrar diálogo de estimación
    try {
      const r = await axios.get(`${process.env.REACT_APP_BACKEND_URL}/api/automatizacion/ia-config`, {
        headers: { Authorization: `Bearer ${localStorage.getItem('lexduo_token')}` }
      });
      setIaConfigActual(r.data);
    } catch (e) {
      setIaConfigActual({ proveedor: 'anthropic', modelo: 'claude-sonnet-4-5-20250929' });
    }
    setEstimacionLoteOpen(true);
  };

  const handleProcessAll = async () => {
    setProcessingAll(true);
    try {
      const idsToProcess = selectedEmails.size > 0 ? Array.from(selectedEmails) : null;
      const res = await automatizacionAPI.processAll(idsToProcess);
      if (res.data.job_id) {
        setProcessAllJob({ ...res.data, estado: 'procesando', procesados: 0, exitosos: 0, errores: 0, email_actual: '' });
        // Start polling. Un fallo puntual (429 del rate limit, corte de red) NO
        // debe borrar el indicador: el lote sigue corriendo en el servidor.
        let fallosSeguidos = 0;
        const pollInterval = setInterval(async () => {
          try {
            const progressRes = await axios.get(`${process.env.REACT_APP_BACKEND_URL}/api/automatizacion/emails-procuradores/process-all/progress/${res.data.job_id}`, {
              headers: { Authorization: `Bearer ${localStorage.getItem('lexduo_token')}` }
            });
            const job = progressRes.data;
            fallosSeguidos = 0;
            setProcessAllJob(prev => ({ ...prev, ...job }));
            if (job.estado === 'completado' || job.estado === 'cancelado' || job.estado === 'error') {
              clearInterval(pollInterval);
              setProcessingAll(false);
              loadEmails();
              loadStats();
              if (job.estado === 'completado') {
                toast({ title: 'Procesamiento completado', description: `${job.exitosos} procesados, ${job.errores} errores` });
              } else if (job.estado === 'cancelado') {
                toast({ title: 'Procesamiento cancelado', description: `${job.exitosos} procesados antes de cancelar` });
              }
              setTimeout(() => { setProcessAllJob(null); setSelectedEmails(new Set()); }, 3000);
            }
          } catch (err) {
            // 429 (rate limit) o error de red: reintentar. Solo nos rendimos
            // tras 10 fallos seguidos (~30 s) para no dejar el aviso colgado.
            fallosSeguidos += 1;
            if (fallosSeguidos >= 10) {
              clearInterval(pollInterval);
              setProcessingAll(false);
              setProcessAllJob(null);
              loadEmails();
              loadStats();
            }
          }
        }, 3000);
      } else {
        toast({ title: 'Info', description: res.data.message });
        setProcessingAll(false);
      }
    } catch (err) {
      toast({ title: 'Error', description: 'Error iniciando procesamiento', variant: 'destructive' });
      setProcessingAll(false);
    }
  };

  const handleCancelProcessAll = async () => {
    if (!processAllJob?.job_id) return;
    try {
      await axios.post(`${process.env.REACT_APP_BACKEND_URL}/api/automatizacion/emails-procuradores/process-all/cancel/${processAllJob.job_id}`, {}, {
        headers: { Authorization: `Bearer ${localStorage.getItem('lexduo_token')}` }
      });
      toast({ title: 'Cancelación solicitada' });
    } catch (err) {
      toast({ title: 'Error cancelando', variant: 'destructive' });
    }
  };

  // Process single with IA
  const handleProcessIA = async (emailId) => {
    setProcessingIA(emailId);
    try {
      await automatizacionAPI.processIA(emailId);
      toast({ title: 'Email procesado con IA' });
      loadEmails();
      loadStats();
    } catch (err) {
      toast({ title: 'Error', description: 'Error procesando con IA', variant: 'destructive' });
    } finally {
      setProcessingIA(null);
    }
  };

  // Open detail
  const openDetail = async (em) => {
    setDetailDialog({ open: true, email: null });
    setDetailLoading(true);
    try {
      const res = await automatizacionAPI.getEmail(em.id);
      setDetailDialog({ open: true, email: res.data });
    } catch (err) {
      toast({ title: 'Error', description: 'Error cargando detalle', variant: 'destructive' });
      setDetailDialog({ open: false, email: null });
    } finally {
      setDetailLoading(false);
    }
  };

  // Open confirm
  // Helper para cambiar el PDF que se muestra embebido en la preview
  const selectPreviewPdf = (emailId, idx) => {
    const url = automatizacionAPI.getPreviewPdfUrl(emailId, idx);
    const token = localStorage.getItem('lexduo_token');
    setPreviewPdfIdx(idx);
    setPreviewPdfUrl(`${url}?token=${token}`);
  };

  const openConfirm = (em) => {
    const rawTitle = em.titulo_confirmado || em.ia_titulo || em.asunto || '';
    const numInterno = user?.numero_interno || '';
    // Auto uppercase + append numero interno
    const formattedTitle = rawTitle.toUpperCase() + (numInterno ? ` (${numInterno})` : '');
    setConfirmForm({
      expediente_id: em.expediente_id_confirmado || em.ia_expediente_id || '',
      titulo: formattedTitle,
      resumen: em.resumen_confirmado || em.ia_resumen || '',
      tipo_tramite: (em.tipo_tramite_confirmado || em.ia_tipo_tramite || 'tramite').toLowerCase(),
      tiene_plazo: em.tiene_plazo || false,
      fecha_limite: em.fecha_limite ? em.fecha_limite.split('T')[0] : '',
      es_urgente: em.es_urgente || false,
      notificar_cliente: true,
    });
    if (em.ia_expediente_id) {
      setSelectedExp({ id: em.ia_expediente_id, numero_expediente: em.ia_expediente_numero || '' });
    } else {
      setSelectedExp(null);
    }
    setExpSearch('');
    setExpResults([]);
    // Reset preview PDF to first attachment
    setPreviewPdfIdx(0);
    if (em.adjuntos && em.adjuntos.length > 0) {
      const url = automatizacionAPI.getPreviewPdfUrl(em.id, 0);
      const token = localStorage.getItem('lexduo_token');
      setPreviewPdfUrl(`${url}?token=${token}`);
    } else {
      setPreviewPdfUrl('');
    }
    setConfirmDialog({ open: true, email: em });
    // v38: reset tab actuaciones cada vez que se abre
    setRightPanelTab('avisos');
    setExpandedActuacionId(null);
    setActuacionPreview({ adjuntoId: null, url: '', nombre: '' });
  };

  // v38: cargar actuaciones previas del expediente cuando cambia selectedExp
  useEffect(() => {
    if (!confirmDialog.open || !selectedExp?.id) {
      setActuacionesPrevias([]);
      return;
    }
    let cancelled = false;
    setLoadingActuaciones(true);
    expedientesAPI.getActuaciones(selectedExp.id)
      .then(res => {
        if (cancelled) return;
        setActuacionesPrevias(Array.isArray(res.data) ? res.data : []);
      })
      .catch(err => {
        if (cancelled) return;
        console.error('Error cargando actuaciones previas:', err);
        setActuacionesPrevias([]);
      })
      .finally(() => { if (!cancelled) setLoadingActuaciones(false); });
    return () => { cancelled = true; };
  }, [confirmDialog.open, selectedExp?.id]);

  // v38: expandir actuacion -> cargar sus adjuntos (lazy)
  const toggleExpandActuacion = async (actuacionId) => {
    if (expandedActuacionId === actuacionId) {
      setExpandedActuacionId(null);
      return;
    }
    setExpandedActuacionId(actuacionId);
    setActuacionPreview({ adjuntoId: null, url: '', nombre: '' });
    if (!adjuntosPorActuacion[actuacionId]) {
      try {
        const res = await adjuntosAPI.getByActuacion(actuacionId);
        setAdjuntosPorActuacion(prev => ({ ...prev, [actuacionId]: Array.isArray(res.data) ? res.data : [] }));
      } catch (err) {
        console.error('Error cargando adjuntos de actuacion:', err);
        setAdjuntosPorActuacion(prev => ({ ...prev, [actuacionId]: [] }));
      }
    }
  };

  // v38: previsualizar adjunto de una actuacion previa
  const previewAdjuntoActuacion = (adj) => {
    if (actuacionPreview.adjuntoId === adj.id) {
      setActuacionPreview({ adjuntoId: null, url: '', nombre: '' });
      return;
    }
    const token = localStorage.getItem('lexduo_token');
    const url = adjuntosAPI.getPreviewUrl(adj.id) + `?token=${token}`;
    setActuacionPreview({ adjuntoId: adj.id, url, nombre: adj.nombre });
  };

  // Search expedientes
  const searchExpedientes = async (q) => {
    setExpSearch(q);
    if (q.length < 2) {
      setExpResults([]);
      return;
    }
    try {
      const res = await automatizacionAPI.buscarExpedientes(q);
      setExpResults(res.data.expedientes || []);
    } catch (err) {
      console.error('Error buscando expedientes:', err);
    }
  };

  // ===== Post-confirmation flow helpers (replicates NuevaActuacionIA) =====
  const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

  const handleCambiarIdiomaDecreto = async (idioma) => {
    if (!decretoInfo || idioma === decretoIdioma) return;
    setCambiandoIdioma(true);
    try {
      const { emailId } = postConfirmAdjuntosRef.current;
      const res = await automatizacionAPI.borradorDecreto(emailId, {
        expediente_id: postConfirmExpIdRef.current,
        tipo_tramite: decretoInfo.tipo,
        idioma,
        importe_costas: decretoInfo.importe_costas,
        importe_intereses: decretoInfo.importe_intereses,
      });
      setEmailSentenciaAsunto(res.data.asunto || '');
      setEmailSentenciaCuerpo(res.data.cuerpo || '');
      setDecretoIdioma(idioma);
    } catch (err) {
      toast({ title: 'Error', description: "No s'ha pogut canviar l'idioma", variant: 'destructive' });
    } finally {
      setCambiandoIdioma(false);
    }
  };

  const proceedAfterEmail = () => {
    setDecretoInfo(null);
    const pending = pendingDatosFaltantesRef.current;
    if (pending && pending.length > 0) {
      setDatosFaltantes(pending);
      const sel = {};
      pending.forEach((d, i) => { sel[i] = true; });
      setDatosSeleccionados(sel);
      setPostConfirmStep('datos_faltantes');
    } else {
      setPostConfirmStep('idle');
    }
  };

  const handleSendEmailSentencia = async () => {
    if (!emailSentenciaDestinatario || !emailSentenciaCuerpo.trim()) {
      toast({ title: 'Error', description: 'Falten dades per enviar l\'email', variant: 'destructive' });
      return;
    }
    setSendingEmailSentencia(true);
    try {
      const { emailId } = postConfirmAdjuntosRef.current;

      // Use dedicated automatizacion endpoint that downloads PDF from S3 server-side
      const res = await automatizacionAPI.enviarEmailCliente(emailId, {
        destinatario_email: emailSentenciaDestinatario,
        asunto: emailSentenciaAsunto,
        cuerpo: emailSentenciaCuerpo,
        adjunto_idx: 0,
      });

      if (res.data.success) {
        toast({
          title: 'Email enviat',
          description: res.data.message || `Email enviat a ${emailSentenciaDestinatario}`,
        });
      } else {
        toast({ title: 'Error', description: "No s'ha pogut enviar l'email", variant: 'destructive' });
      }
    } catch (err) {
      console.error('Error enviando email sentencia:', err);
      const detail = err.response?.data?.detail || "Error de connexió enviant l'email";
      toast({ title: 'Error', description: detail, variant: 'destructive' });
    } finally {
      setSendingEmailSentencia(false);
      proceedAfterEmail();
    }
  };

  const handleSkipEmailSentencia = () => {
    proceedAfterEmail();
  };

  const handleIncorporarDatos = async () => {
    const datosAIncorporar = datosFaltantes
      .filter((_, i) => datosSeleccionados[i])
      .map(d => ({
        campo: d.campo,
        valor_detectado: d.valor_detectado,
        tipo: d.tipo,
        rol_participante: d.rol_participante,
        contacto_id_existente: d.contacto_id_existente || null,
      }));

    if (datosAIncorporar.length === 0) {
      handleSkipDatos();
      return;
    }

    setActualizandoDatos(true);
    try {
      const token = localStorage.getItem('lexduo_token');
      const res = await fetch(`${API}/actuaciones-ia/actualizar-expediente-datos`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          expediente_id: postConfirmExpIdRef.current,
          datos: datosAIncorporar,
        }),
      });

      if (res.ok) {
        const result = await res.json();
        toast({ title: 'Expediente actualizado', description: `Se incorporaron ${result.actualizados.length} dato(s) al expediente` });
      } else {
        const err = await res.json().catch(() => ({}));
        toast({ title: 'Error', description: err.detail || 'No se pudieron incorporar los datos', variant: 'destructive' });
      }
    } catch (err) {
      console.error('Error actualizando expediente:', err);
      toast({ title: 'Error', description: 'Error al incorporar datos', variant: 'destructive' });
    } finally {
      setActualizandoDatos(false);
      setPostConfirmStep('idle');
    }
  };

  const handleSkipDatos = () => {
    setPostConfirmStep('idle');
  };

  // Confirm email
  const handleConfirm = async () => {
    if (!confirmForm.expediente_id || !confirmForm.titulo) {
      toast({ title: 'Faltan datos', description: 'Expediente y título son obligatorios', variant: 'destructive' });
      return;
    }
    setConfirming(true);
    try {
      const tipoTramite = confirmForm.tipo_tramite || '';
      const tipoLower = tipoTramite.toLowerCase();
      const TIPOS_NOTIFICA_CLIENTE = ['sentencia', 'recurso_apelacion_banco', 'senalamiento',
        'decreto_costas', 'decreto_intereses', 'decreto_costas_intereses',
        'tasacion_costas'];
      const requiereNotificacion = TIPOS_NOTIFICA_CLIENTE.includes(tipoLower);
      const notificarCliente = requiereNotificacion ? confirmForm.notificar_cliente : false;

      // Save email adjuntos data before closing confirm dialog (needed for PDF attachment)
      const emailAdjuntos = confirmDialog.email?.adjuntos || [];
      const emailId = confirmDialog.email?.id;

      const res = await automatizacionAPI.confirmEmail(emailId, {
        expediente_id: parseInt(confirmForm.expediente_id),
        titulo: confirmForm.titulo.toUpperCase(),
        resumen: confirmForm.resumen,
        tipo_tramite: tipoTramite,
        tiene_plazo: confirmForm.tiene_plazo,
        fecha_limite: confirmForm.fecha_limite || null,
        es_urgente: confirmForm.es_urgente,
        notificar_cliente: notificarCliente,
      });

      const expedienteId = res.data.expediente_id_confirmado || parseInt(confirmForm.expediente_id);
      const resumenTexto = confirmForm.resumen || '';
      const tituloTexto = confirmForm.titulo || '';
      postConfirmExpIdRef.current = expedienteId;

      // Store adjuntos info for PDF attachment later
      postConfirmAdjuntosRef.current = { emailAdjuntos, emailId };

      // Determine if we need post-confirm steps
      const needsEmail = requiereNotificacion && notificarCliente;
      const hasDatosExpediente = res.data.datos_expediente && Object.keys(res.data.datos_expediente).length > 0;

      if (needsEmail || hasDatosExpediente) {
        // IMMEDIATELY show loading dialog - don't go back to list
        setPostConfirmStep('loading');
        setConfirmDialog({ open: false, email: null });

        // 1. Detectar datos faltantes (in background)
        let detectedDatosFaltantes = [];
        if (hasDatosExpediente && expedienteId) {
          try {
            const token = localStorage.getItem('lexduo_token');
            const detectForm = new FormData();
            detectForm.append('expediente_id', expedienteId);
            detectForm.append('datos_expediente', JSON.stringify(res.data.datos_expediente));

            const detectRes = await fetch(`${API}/actuaciones-ia/detectar-datos-faltantes`, {
              method: 'POST',
              headers: { 'Authorization': `Bearer ${token}` },
              body: detectForm,
            });

            if (detectRes.ok) {
              const detectData = await detectRes.json();
              detectedDatosFaltantes = detectData.datos_faltantes || [];
            }
          } catch (err) {
            console.error('Error detectando datos faltantes:', err);
          }
        }
        pendingDatosFaltantesRef.current = detectedDatosFaltantes;

        // 2. Si es notificable (sentencia / recurso_apelacion_banco / senalamiento) → preparar borrador email
        if (needsEmail && expedienteId) {
          setGeneratingEmail(true);
          setPostConfirmStep('email_sentencia');

          // El backend ya devuelve email_draft con asunto/cuerpo prellenados para los 3 tipos.
          // Para SENTENCIA: además llamamos al endpoint LLM /actuaciones-ia/generar-email-sentencia
          // que genera un cuerpo personalizado por IA con datos del expediente.
          // Para RECURSO_APELACION_BANCO y SENALAMIENTO: usamos directamente el draft del backend.
          const backendDraft = res.data.email_draft;
          setDecretoInfo(res.data.decreto || null);
          setDecretoIdioma('ca');

          if (res.data.decreto?.bloqueado) {
            // Costes imposades al nostre client: no es comunica mai al client
            setEmailSentenciaData(null);
            setEmailSentenciaAsunto('');
            setEmailSentenciaCuerpo('');
            setEmailSentenciaDestinatario('');
            setGeneratingEmail(false);
          } else if (tipoLower !== 'sentencia' && backendDraft) {
            // Tipos nuevos: usar plantilla hardcoded del backend
            setEmailSentenciaData({
              success: true,
              asunto: backendDraft.asunto || '',
              cuerpo: backendDraft.cuerpo || '',
              destinatario_email: backendDraft.destinatario_email || '',
              destinatario_nombre: backendDraft.destinatario_nombre || '',
              expediente_numero: backendDraft.expediente_numero || '',
              consulta_id: backendDraft.consulta_id || null,
            });
            setEmailSentenciaAsunto(backendDraft.asunto || '');
            setEmailSentenciaCuerpo(backendDraft.cuerpo || '');
            setEmailSentenciaDestinatario(backendDraft.destinatario_email || '');
            setGeneratingEmail(false);
          } else {
            // Sentencia (flujo original): generar con IA via endpoint actuaciones-ia
            try {
              const token = localStorage.getItem('lexduo_token');
              const emailRes = await fetch(`${API}/actuaciones-ia/generar-email-sentencia`, {
                method: 'POST',
                headers: {
                  'Authorization': `Bearer ${token}`,
                  'Content-Type': 'application/json',
                },
                body: JSON.stringify({
                  expediente_id: expedienteId,
                  observaciones: resumenTexto,
                  titulo: tituloTexto,
                }),
              });

              const data = await emailRes.json();
              if (data.success) {
                setEmailSentenciaData(data);
                setEmailSentenciaAsunto(data.asunto);
                setEmailSentenciaCuerpo(data.cuerpo);
                setEmailSentenciaDestinatario(data.destinatario_email);
              } else if (backendDraft) {
                // Fallback al draft del backend si el LLM falla
                setEmailSentenciaData({ success: true, ...backendDraft });
                setEmailSentenciaAsunto(backendDraft.asunto || '');
                setEmailSentenciaCuerpo(backendDraft.cuerpo || '');
                setEmailSentenciaDestinatario(backendDraft.destinatario_email || '');
              } else {
                toast({ title: 'Aviso', description: data.message || "No s'ha pogut generar l'email. Podeu ometre aquest pas.", variant: 'default' });
              }
            } catch (err) {
              console.error('Error generando email sentencia:', err);
              if (backendDraft) {
                setEmailSentenciaData({ success: true, ...backendDraft });
                setEmailSentenciaAsunto(backendDraft.asunto || '');
                setEmailSentenciaCuerpo(backendDraft.cuerpo || '');
                setEmailSentenciaDestinatario(backendDraft.destinatario_email || '');
              } else {
                toast({ title: 'Aviso', description: "Error generant l'email de sentència", variant: 'default' });
              }
            } finally {
              setGeneratingEmail(false);
            }
          }
        } else {
          // No email step → go directly to datos faltantes
          if (detectedDatosFaltantes.length > 0) {
            setDatosFaltantes(detectedDatosFaltantes);
            const sel = {};
            detectedDatosFaltantes.forEach((d, i) => { sel[i] = true; });
            setDatosSeleccionados(sel);
            setPostConfirmStep('datos_faltantes');
          } else {
            setPostConfirmStep('idle');
          }
        }
      } else {
        // No post-confirm steps needed, go back to list
        toast({ title: 'Actuacion creada', description: res.data.message });
        setConfirmDialog({ open: false, email: null });
      }

      loadEmails();
      loadStats();

    } catch (err) {
      toast({ title: 'Error', description: err.response?.data?.detail || 'Error confirmando', variant: 'destructive' });
    } finally {
      setConfirming(false);
    }
  };

  // Save draft (guardar borrador)
  const handleSaveDraft = async () => {
    if (!confirmDialog.email) return;
    setSavingDraft(true);
    try {
      await automatizacionAPI.editEmail(confirmDialog.email.id, {
        expediente_id: confirmForm.expediente_id ? parseInt(confirmForm.expediente_id) : null,
        titulo: confirmForm.titulo,
        resumen: confirmForm.resumen,
        tipo_tramite: confirmForm.tipo_tramite,
        tiene_plazo: confirmForm.tiene_plazo,
        fecha_limite: confirmForm.fecha_limite || null,
        es_urgente: confirmForm.es_urgente,
        es_borrador: true,
      });
      toast({ title: 'Borrador guardado', description: 'Los datos se han guardado sin confirmar la actuacion' });
      setConfirmDialog({ open: false, email: null });
      loadEmails();
    } catch (err) {
      toast({ title: 'Error', description: 'Error guardando borrador', variant: 'destructive' });
    } finally {
      setSavingDraft(false);
    }
  };

  // Re-analyze with IA
  const handleReanalyze = async () => {
    if (!confirmDialog.email) return;
    setReanalyzing(true);
    try {
      await automatizacionAPI.reprocessEmail(confirmDialog.email.id);
      await automatizacionAPI.processIA(confirmDialog.email.id);
      toast({ title: 'Re-analisis completado', description: 'La IA ha vuelto a analizar el email' });
      // Reload the email data and re-open
      const res = await automatizacionAPI.getEmail(confirmDialog.email.id);
      const updatedEmail = res.data;
      openConfirm(updatedEmail);
      loadEmails();
      loadStats();
    } catch (err) {
      toast({ title: 'Error', description: 'Error re-analizando con IA', variant: 'destructive' });
    } finally {
      setReanalyzing(false);
    }
  };

  // Re-descargar adjuntos desde IMAP -> S3
  const handleRedownloadAttachments = async () => {
    setRedownloading(true);
    try {
      const res = await automatizacionAPI.redownloadAttachments();
      const data = res.data;
      toast({
        title: 'Re-descarga completada',
        description: `${data.actualizados} de ${data.total_sin_s3} emails actualizados. Errores: ${data.errores}`,
      });
      loadEmails();
    } catch (err) {
      toast({ title: 'Error', description: err.response?.data?.detail || 'Error re-descargando adjuntos', variant: 'destructive' });
    } finally {
      setRedownloading(false);
    }
  };

  // Discard
  const handleDiscard = async (emailId) => {
    try {
      await automatizacionAPI.discardEmail(emailId);
      toast({ title: 'Email descartado' });
      loadEmails();
      loadStats();
      setDetailDialog({ open: false, email: null });
    } catch (err) {
      toast({ title: 'Error', variant: 'destructive' });
    }
  };

  // Restore (desde descartados)
  const handleRestore = async (emailId) => {
    try {
      await automatizacionAPI.restoreEmail(emailId);
      toast({ title: 'Email recuperado', description: 'Vuelve a estar disponible en "Sin procesar"' });
      loadEmails();
      loadStats();
      setDetailDialog({ open: false, email: null });
    } catch (err) {
      toast({ title: 'Error', description: err.response?.data?.detail || 'No se pudo recuperar', variant: 'destructive' });
    }
  };

  // Email Composer: Apply template
  const handleEmailPlantillaSelect = (plantillaId) => {
    const plantilla = emailPlantillas.find(p => p.id === parseInt(plantillaId));
    if (plantilla) {
      let cuerpo = plantilla.cuerpo || '';
      cuerpo = cuerpo.replace(/\{\{cliente_nombre\}\}/g, emailComposer.destinatario_nombre || '');
      cuerpo = cuerpo.replace(/\{cliente_nombre\}/g, emailComposer.destinatario_nombre || '');
      cuerpo = cuerpo.replace(/\{\{expediente_numero\}\}/g, emailComposer.expediente_numero || '');
      cuerpo = cuerpo.replace(/\{expediente_numero\}/g, emailComposer.expediente_numero || '');
      setEmailComposer(prev => ({
        ...prev,
        asunto: plantilla.asunto || prev.asunto,
        cuerpo: cuerpo,
      }));
    }
  };

  // Email Composer: Send email
  const handleEmailSend = async () => {
    if (!emailComposer.destinatario_email || !emailComposer.cuerpo) {
      toast({ title: 'Error', description: 'Complete el email destinatario y el cuerpo del mensaje', variant: 'destructive' });
      return;
    }
    setEmailSending(true);
    try {
      const res = await atencionClienteAPI.createRespuesta(emailComposer.consulta_id, {
        destinatario_email: emailComposer.destinatario_email,
        asunto: emailComposer.asunto,
        cuerpo: emailComposer.cuerpo,
        enviar_ahora: true,
        adjuntos: [],
      });
      if (res.data.enviado) {
        toast({ title: 'Email enviado correctamente', description: `Enviado a ${emailComposer.destinatario_email}` });
        setEmailComposer({ open: false, consulta_id: null, destinatario_email: '', destinatario_nombre: '', asunto: '', cuerpo: '', expediente_numero: '' });
      } else {
        toast({ title: 'Error al enviar', description: res.data.error || 'No se pudo enviar el email. Verifique la configuración SMTP.', variant: 'destructive' });
      }
    } catch (err) {
      toast({ title: 'Error', description: 'No se pudo enviar el email', variant: 'destructive' });
    } finally {
      setEmailSending(false);
    }
  };

  // Email Composer: Close (save for later)
  const handleEmailSaveLater = () => {
    toast({ title: 'Borrador guardado', description: `El borrador queda en Atención al Cliente para su envío posterior (Consulta #${emailComposer.consulta_id})` });
    setEmailComposer({ open: false, consulta_id: null, destinatario_email: '', destinatario_nombre: '', asunto: '', cuerpo: '', expediente_numero: '' });
  };


  // Save config
  const handleSaveConfig = async () => {
    if (!configForm.imap_user || !configForm.imap_password) {
      toast({ title: 'Complete los campos obligatorios', variant: 'destructive' });
      return;
    }
    setSavingConfig(true);
    try {
      await automatizacionAPI.saveConfig(configForm);
      toast({ title: 'Configuración guardada' });
      loadConfig();
      setConfigDialog(false);
    } catch (err) {
      toast({ title: 'Error', description: 'Error guardando configuración', variant: 'destructive' });
    } finally {
      setSavingConfig(false);
    }
  };

  // Test connection
  const handleTestConnection = async () => {
    setTestingConnection(true);
    try {
      const res = await automatizacionAPI.testConnection(configForm);
      if (res.data.success) {
        toast({ title: 'Conexión exitosa', description: res.data.message });
      } else {
        toast({ title: 'Error de conexión', description: res.data.message, variant: 'destructive' });
      }
    } catch (err) {
      toast({ title: 'Error', description: 'No se pudo conectar', variant: 'destructive' });
    } finally {
      setTestingConnection(false);
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    try {
      return new Date(dateStr).toLocaleString('es-ES', {
        day: '2-digit', month: '2-digit', year: 'numeric',
        hour: '2-digit', minute: '2-digit'
      });
    } catch { return dateStr; }
  };

  const formatBytes = (bytes) => {
    if (!bytes) return '0 B';
    const k = 1024;
    const sizes = ['B', 'KB', 'MB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(1)) + ' ' + sizes[i];
  };

  return (
    <div className="p-4 lg:p-6 space-y-4" data-testid="automatizacion-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3">
        <div>
          <h1 className="text-xl lg:text-2xl font-bold text-gray-900 flex items-center gap-2">
            <Zap className="h-5 w-5 lg:h-6 lg:w-6 text-amber-500" />
            Automatización
          </h1>
          <p className="text-sm text-gray-500 mt-1">Gestión automatizada de emails</p>
        </div>
      </div>

      {/* Main section selector */}
      <div className="flex items-center gap-1 bg-gray-100 rounded-lg p-1 w-fit" data-testid="main-section-selector">
        {canProcuradoresAny && (
        <button
          className={`px-4 py-2 rounded-md text-sm font-medium transition-all ${mainSection === 'procuradores' ? 'bg-white shadow-sm text-gray-900' : 'text-gray-500 hover:text-gray-700'}`}
          onClick={() => setMainSection('procuradores')}
          data-testid="section-procuradores"
        >
          <FileText className="h-4 w-4 inline mr-1.5" />
          Emails Procuradores
        </button>
        )}
        {canClientesAny && (
        <button
          className={`px-4 py-2 rounded-md text-sm font-medium transition-all ${mainSection === 'clientes' ? 'bg-white shadow-sm text-gray-900' : 'text-gray-500 hover:text-gray-700'}`}
          onClick={() => setMainSection('clientes')}
          data-testid="section-clientes"
        >
          <User className="h-4 w-4 inline mr-1.5" />
          Atención al Cliente
        </button>
        )}
        <button
          className={`px-4 py-2 rounded-md text-sm font-medium transition-all ${mainSection === 'abogados' ? 'bg-white shadow-sm text-gray-900' : 'text-gray-500 hover:text-gray-700'}`}
          onClick={() => setMainSection('abogados')}
          data-testid="section-abogados"
        >
          <FileText className="h-4 w-4 inline mr-1.5" />
          Abogados
        </button>
      </div>

      {/* Seccion: Atencion al Cliente */}
      {mainSection === 'clientes' && (
        <EmailsClientes />
      )}

      {/* Seccion: Abogados */}
      {mainSection === 'abogados' && (
        <EmailsAbogados />
      )}

      {/* Seccion: Procuradores (contenido original) */}
      {mainSection === 'procuradores' && (
      <>
      {/* Pause banner */}
      {canConfig && config && !config.activo && (
        <div className="flex items-center justify-between bg-amber-50 border border-amber-200 rounded-lg px-4 py-3" data-testid="pause-banner">
          <div className="flex items-center gap-2 text-amber-800">
            <Pause className="h-5 w-5" />
            <span className="font-medium text-sm">La automatización está pausada</span>
            <span className="text-xs text-amber-600">— No se descargarán ni procesarán emails nuevos</span>
          </div>
          <Button
            size="sm"
            variant="outline"
            className="border-amber-400 text-amber-700 hover:bg-amber-100"
            onClick={handleToggleActive}
            data-testid="resume-from-banner-btn"
          >
            <Play className="h-4 w-4 mr-1" /> Reanudar
          </Button>
        </div>
      )}

      {/* Procuradores Header */}
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3">
        <div>
          <h2 className="text-lg font-semibold text-gray-800 flex items-center gap-2">
            <Mail className="h-5 w-5 text-blue-500" />
            Emails Procuradores
            {config && (
              <Badge className={config.activo ? 'bg-green-100 text-green-700 text-xs ml-2' : 'bg-amber-100 text-amber-700 text-xs ml-2'}>
                {config.activo ? 'Activa' : 'Pausada'}
              </Badge>
            )}
          </h2>
        </div>
        <div className="flex items-center gap-2">
          {canConfig && config && (
            <Button
              variant="outline"
              size="sm"
              onClick={handleToggleActive}
              className={config.activo ? 'text-amber-600 hover:bg-amber-50' : 'text-green-600 hover:bg-green-50'}
              data-testid="toggle-active-btn"
            >
              {config.activo ? (
                <><Pause className="h-4 w-4 mr-1" /> Pausar</>
              ) : (
                <><Play className="h-4 w-4 mr-1" /> Reanudar</>
              )}
            </Button>
          )}
          {canConfig && (
          <Button
            variant="outline"
            size="sm"
            onClick={() => setConfigDialog(true)}
            data-testid="config-btn"
          >
            <Settings className="h-4 w-4 mr-1" /> Configuración
          </Button>
          )}
          {canConfig && (
          <Button
            variant="outline"
            size="sm"
            onClick={() => window.open('/configuracion/atencion-cliente?tab=plantillas-automatizacion', '_blank')}
            data-testid="plantillas-notificacion-btn"
            title="Editar plantillas de notificación al cliente (sentencia, recurs apel·lació, assenyalament)"
          >
            <FileText className="h-4 w-4 mr-1" /> Plantillas
          </Button>
          )}
          <Button
            size="sm"
            onClick={handleFetchEmails}
            disabled={fetching || (config && !config.activo)}
            data-testid="fetch-emails-btn"
          >
            {fetching ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : <Download className="h-4 w-4 mr-1" />}
            Descargar Emails
          </Button>
        </div>
      </div>

      {/* Panel de consumo IA */}
      <PanelConsumoIA compact />

      {/* Stats Cards */}
      {stats && (
        <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-8 gap-3">
          <Card className="border-l-4 border-l-blue-500">
            <CardContent className="p-3">
              <div className="text-xs text-gray-500">Total</div>
              <div className="text-lg font-bold">{stats.total}</div>
            </CardContent>
          </Card>
          <Card className="border-l-4 border-l-amber-500">
            <CardContent className="p-3">
              <div className="text-xs text-gray-500">Pendientes</div>
              <div className="text-lg font-bold text-amber-600">{stats.pendientes_revision}</div>
            </CardContent>
          </Card>
          <Card className={`border-l-4 border-l-red-600 ${stats.urgentes > 0 ? 'ring-1 ring-red-300' : ''}`}>
            <CardContent className="p-3">
              <div className="text-xs text-gray-500">Urgentes</div>
              <div className={`text-lg font-bold text-red-600 ${stats.urgentes > 0 ? 'animate-pulse' : ''}`}>{stats.urgentes}</div>
            </CardContent>
          </Card>
          <Card className="border-l-4 border-l-sky-500">
            <CardContent className="p-3">
              <div className="text-xs text-gray-500">Borradores</div>
              <div className="text-lg font-bold text-sky-600">{stats.borradores}</div>
            </CardContent>
          </Card>
          <Card className="border-l-4 border-l-green-500">
            <CardContent className="p-3">
              <div className="text-xs text-gray-500">Confirmados</div>
              <div className="text-lg font-bold text-green-600">{stats.confirmados}</div>
            </CardContent>
          </Card>
          <Card className="border-l-4 border-l-indigo-400">
            <CardContent className="p-3">
              <div className="text-xs text-gray-500">Sin procesar</div>
              <div className="text-lg font-bold text-indigo-600">{stats.descargados_sin_procesar}</div>
            </CardContent>
          </Card>
          <Card className="border-l-4 border-l-red-400">
            <CardContent className="p-3">
              <div className="text-xs text-gray-500">Errores</div>
              <div className="text-lg font-bold text-red-600">{stats.errores}</div>
            </CardContent>
          </Card>
          <Card className="border-l-4 border-l-gray-400">
            <CardContent className="p-3">
              <div className="text-xs text-gray-500">Descartados</div>
              <div className="text-lg font-bold text-gray-500">{stats.descartados}</div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList>
          {canEmails && (
          <TabsTrigger value="emails" data-testid="tab-emails">
            <Mail className="h-4 w-4 mr-1" /> Emails Procuradores
          </TabsTrigger>
          )}
          {canEmails && (
          <TabsTrigger value="historial" data-testid="tab-historial">
            <FileText className="h-4 w-4 mr-1" /> Historial
          </TabsTrigger>
          )}
          {canConfig && (
          <TabsTrigger value="reglas" data-testid="tab-reglas">
            <List className="h-4 w-4 mr-1" /> Reglas
          </TabsTrigger>
          )}
          {canEstadisticas && (
          <TabsTrigger value="estadisticas" data-testid="tab-estadisticas">
            <BarChart3 className="h-4 w-4 mr-1" /> Estadísticas
          </TabsTrigger>
          )}
        </TabsList>

        <TabsContent value="emails" className="mt-4">
          {/* Sub-pestañas por estado */}
          <div className="flex flex-wrap items-center gap-1 mb-4 border-b pb-2">
            <button
              className={`px-3 py-1.5 rounded-t text-sm font-medium transition-colors ${emailSubTab === 'sin_procesar' ? 'bg-indigo-100 text-indigo-800 border border-b-0 border-indigo-300' : 'text-gray-500 hover:text-gray-700'}`}
              onClick={() => handleSubTabChange('sin_procesar')}
              data-testid="subtab-sin-procesar"
            >
              <FileSearch className="h-3.5 w-3.5 inline mr-1" />
              Sin procesar {stats?.descargados_sin_procesar > 0 && <span className="ml-1 bg-indigo-500 text-white text-xs rounded-full px-1.5">{stats.descargados_sin_procesar}</span>}
            </button>
            <button
              className={`px-3 py-1.5 rounded-t text-sm font-medium transition-colors ${emailSubTab === 'pendientes' ? 'bg-amber-100 text-amber-800 border border-b-0 border-amber-300' : 'text-gray-500 hover:text-gray-700'}`}
              onClick={() => handleSubTabChange('pendientes')}
              data-testid="subtab-pendientes"
            >
              <Clock className="h-3.5 w-3.5 inline mr-1" />
              Pendientes {stats?.pendientes_revision > 0 && <span className="ml-1 bg-amber-500 text-white text-xs rounded-full px-1.5">{stats.pendientes_revision}</span>}
            </button>
            <button
              className={`px-3 py-1.5 rounded-t text-sm font-medium transition-colors ${emailSubTab === 'urgentes' ? 'bg-red-100 text-red-800 border border-b-0 border-red-300' : 'text-gray-500 hover:text-gray-700'}`}
              onClick={() => handleSubTabChange('urgentes')}
              data-testid="subtab-urgentes"
            >
              <AlertTriangle className="h-3.5 w-3.5 inline mr-1" />
              Urgentes {stats?.urgentes > 0 && <span className="ml-1 bg-red-600 text-white text-xs rounded-full px-1.5 animate-pulse">{stats.urgentes}</span>}
            </button>
            <button
              className={`px-3 py-1.5 rounded-t text-sm font-medium transition-colors ${emailSubTab === 'borradores' ? 'bg-sky-100 text-sky-800 border border-b-0 border-sky-300' : 'text-gray-500 hover:text-gray-700'}`}
              onClick={() => handleSubTabChange('borradores')}
              data-testid="subtab-borradores"
            >
              <Save className="h-3.5 w-3.5 inline mr-1" />
              Borradores {stats?.borradores > 0 && <span className="ml-1 bg-sky-500 text-white text-xs rounded-full px-1.5">{stats.borradores}</span>}
            </button>
            <button
              className={`px-3 py-1.5 rounded-t text-sm font-medium transition-colors ${emailSubTab === 'confirmados' ? 'bg-green-100 text-green-800 border border-b-0 border-green-300' : 'text-gray-500 hover:text-gray-700'}`}
              onClick={() => handleSubTabChange('confirmados')}
              data-testid="subtab-confirmados"
            >
              <CheckCircle className="h-3.5 w-3.5 inline mr-1" />
              Confirmados {stats?.confirmados > 0 && <span className="ml-1 bg-green-500 text-white text-xs rounded-full px-1.5">{stats.confirmados}</span>}
            </button>
            <button
              className={`px-3 py-1.5 rounded-t text-sm font-medium transition-colors ${emailSubTab === 'descartados' ? 'bg-zinc-200 text-zinc-800 border border-b-0 border-zinc-400' : 'text-gray-500 hover:text-gray-700'}`}
              onClick={() => handleSubTabChange('descartados')}
              data-testid="subtab-descartados"
              title="Emails descartados por el usuario. Pueden recuperarse desde aqui."
            >
              <XCircle className="h-3.5 w-3.5 inline mr-1" />
              Descartados {stats?.descartados > 0 && <span className="ml-1 bg-zinc-600 text-white text-xs rounded-full px-1.5">{stats.descartados}</span>}
            </button>
            <button
              className={`px-3 py-1.5 rounded-t text-sm font-medium transition-colors ${emailSubTab === 'archivo' ? 'bg-gray-200 text-gray-800 border border-b-0 border-gray-300' : 'text-gray-500 hover:text-gray-700'}`}
              onClick={() => handleSubTabChange('archivo')}
              data-testid="subtab-archivo"
            >
              <List className="h-3.5 w-3.5 inline mr-1" />
              Archivo
            </button>
            <button
              className={`px-3 py-1.5 rounded-t text-sm font-medium transition-colors ${emailSubTab === 'errores' ? 'bg-rose-100 text-rose-800 border border-b-0 border-rose-300' : 'text-gray-500 hover:text-gray-700'}`}
              onClick={() => handleSubTabChange('errores')}
              data-testid="subtab-errores"
              title="Emails que fallaron por motivos intrinsecos (parseo, adjunto). Revisar y tramitar manualmente."
            >
              <AlertCircle className="h-3.5 w-3.5 inline mr-1" />
              Errores {stats?.errores > 0 && <span className="ml-1 bg-rose-500 text-white text-xs rounded-full px-1.5">{stats.errores}</span>}
            </button>
          </div>

          {/* __V62A__ Bulk confirm dialog */}
          {bulkConfirmDialog && (
            <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" onClick={() => !bulkConfirmLoading && setBulkConfirmDialog(false)}>
              <div className="bg-white rounded-lg shadow-2xl max-w-lg w-full p-5" onClick={(e) => e.stopPropagation()} data-testid="bulk-confirm-dialog">
                <h3 className="font-semibold text-lg mb-3 flex items-center gap-2">
                  <CheckCircle className="w-5 h-5 text-emerald-600" /> Crear actuaciones en masa
                </h3>
                {!bulkConfirmResult ? (
                  <>
                    <p className="text-sm text-gray-700 mb-3">
                      Se crearán actuaciones en el expediente identificado por la IA para los emails seleccionados.
                      Los emails procesados con éxito se archivarán como <b>Confirmados</b>.
                    </p>
                    <div className="bg-amber-50 border border-amber-200 rounded p-2.5 text-xs text-amber-800 mb-3">
                      ⚠ Esta operación no es reversible. Revisa que la IA haya identificado correctamente el expediente.
                    </div>
                    <div className="space-y-2 text-sm">
                      <div className="flex items-center justify-between bg-gray-50 rounded p-2">
                        <span>Emails seleccionados</span>
                        <span className="font-mono font-semibold">{bulkSelectedEmails.size}</span>
                      </div>
                      <label className="flex items-center justify-between gap-2 bg-gray-50 rounded p-2">
                        <span className="text-xs">Sólo si confianza IA ≥</span>
                        <select value={bulkMinConfianza} onChange={(e) => setBulkMinConfianza(parseFloat(e.target.value))} className="text-xs border rounded px-2 py-1">
                          <option value="0">— sin filtro —</option>
                          <option value="0.5">50%</option>
                          <option value="0.7">70%</option>
                          <option value="0.85">85%</option>
                          <option value="0.95">95%</option>
                        </select>
                      </label>
                      <label className="flex items-center gap-2 bg-blue-50 border border-blue-200 rounded p-2 cursor-pointer">
                        <input type="checkbox" checked={bulkNotificarCliente}
                               onChange={(e) => setBulkNotificarCliente(e.target.checked)}
                               className="w-4 h-4 accent-blue-600" data-testid="bulk-notificar-cliente" />
                        <div className="flex-1">
                          <div className="text-xs font-medium text-blue-900">Notificar al cliente cuando proceda</div>
                          <div className="text-[10px] text-blue-700">Solo se envía si la IA detecta: sentencia · auto · señalamiento · recurso apelación contraria</div>
                        </div>
                      </label>
                    </div>
                    <div className="flex justify-end gap-2 mt-4">
                      <Button variant="outline" onClick={() => setBulkConfirmDialog(false)} disabled={bulkConfirmLoading}>Cancelar</Button>
                      <Button onClick={() => runBulkConfirm('selected')} disabled={bulkConfirmLoading || bulkSelectedEmails.size === 0}
                              className="bg-emerald-600 hover:bg-emerald-700 text-white" data-testid="bulk-confirm-execute">
                        {bulkConfirmLoading ? <><Loader2 className="w-4 h-4 mr-1 animate-spin" /> Procesando…</> : <>Crear {bulkSelectedEmails.size} actuación{bulkSelectedEmails.size !== 1 ? 'es' : ''}</>}
                      </Button>
                    </div>
                  </>
                ) : bulkConfirmResult.error ? (
                  <>
                    <div className="bg-red-50 border border-red-200 rounded p-3 text-sm text-red-700">{bulkConfirmResult.error}</div>
                    <div className="flex justify-end mt-3"><Button onClick={() => { setBulkConfirmDialog(false); setBulkConfirmResult(null); }}>Cerrar</Button></div>
                  </>
                ) : (
                  <>
                    <div className="grid grid-cols-3 gap-2 mb-2">
                      <div className="bg-emerald-50 border border-emerald-200 rounded p-2 text-center">
                        <div className="text-2xl font-bold text-emerald-700">{bulkConfirmResult.ok}</div>
                        <div className="text-[10px] text-emerald-700 uppercase">OK</div>
                      </div>
                      <div className="bg-red-50 border border-red-200 rounded p-2 text-center">
                        <div className="text-2xl font-bold text-red-700">{bulkConfirmResult.errors}</div>
                        <div className="text-[10px] text-red-700 uppercase">Errores</div>
                      </div>
                      <div className="bg-amber-50 border border-amber-200 rounded p-2 text-center">
                        <div className="text-2xl font-bold text-amber-700">{bulkConfirmResult.skipped}</div>
                        <div className="text-[10px] text-amber-700 uppercase">Omitidos</div>
                      </div>
                    </div>
                    {/* __V64__ Notificacions enviades al client */}
                    {((bulkConfirmResult.emails_clients_ok ?? 0) + (bulkConfirmResult.emails_clients_error ?? 0)) > 0 && (
                      <div className="grid grid-cols-2 gap-2 mb-3 bg-blue-50 border border-blue-200 rounded p-2">
                        <div className="text-center">
                          <div className="text-xl font-bold text-blue-700">{bulkConfirmResult.emails_clients_ok ?? 0}</div>
                          <div className="text-[10px] text-blue-700 uppercase">📧 Emails al client ✓</div>
                        </div>
                        <div className="text-center">
                          <div className="text-xl font-bold text-red-700">{bulkConfirmResult.emails_clients_error ?? 0}</div>
                          <div className="text-[10px] text-red-700 uppercase">📧 Emails al client ✗</div>
                        </div>
                      </div>
                    )}
                    {bulkConfirmResult.results?.filter(r => r.status !== 'ok' || r.email_error).length > 0 && (
                      <div className="max-h-48 overflow-y-auto bg-gray-50 border rounded text-xs">
                        {bulkConfirmResult.results.filter(r => r.status !== 'ok' || r.email_error).map((r, i) => (
                          <div key={i} className="border-b border-gray-200 last:border-0 p-1.5">
                            <span className={`font-mono mr-2 ${r.status === 'error' ? 'text-red-700' : r.email_error ? 'text-orange-700' : 'text-amber-700'}`}>#{r.email_id}</span>
                            {r.status !== 'ok' && <span className="text-gray-700">{r.reason}</span>}
                            {r.status === 'ok' && r.email_error && (
                              <span className="text-orange-700">📧 Email al client FALLIT: {r.email_error}</span>
                            )}
                          </div>
                        ))}
                      </div>
                    )}
                    <div className="flex justify-end mt-3"><Button onClick={() => { setBulkConfirmDialog(false); setBulkConfirmResult(null); }} className="bg-emerald-600 hover:bg-emerald-700 text-white">Cerrar</Button></div>
                  </>
                )}
              </div>
            </div>
          )}

          {/* Toolbar */}
          <div className="flex flex-col sm:flex-row items-start sm:items-center gap-2 mb-4">
            <div className="relative flex-1 w-full sm:max-w-sm">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
              <Input
                placeholder="Buscar por asunto, remitente, expediente..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                onKeyDown={(e) => { if (e.key === 'Enter') { setCurrentPage(1); loadEmails(); } }}
                className="pl-9"
                data-testid="search-input"
              />
            </div>
            {stats && stats.descargados_sin_procesar > 0 && !processAllJob && (
              <div className="flex items-center gap-2">
                {emailSubTab === 'sin_procesar' && selectedEmails.size > 0 && (
                  <Badge className="bg-purple-100 text-purple-700" data-testid="selected-count">
                    {selectedEmails.size} seleccionados
                  </Badge>
                )}
                <Button
                  variant="outline"
                  size="sm"
                  onClick={handleClickProcessAll}
                  disabled={processingAll}
                  data-testid="process-all-btn"
                >
                  {processingAll ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : <Sparkles className="h-4 w-4 mr-1" />}
                  {selectedEmails.size > 0
                    ? `Procesar seleccionados (${selectedEmails.size})`
                    : `Procesar todos con IA (${stats.descargados_sin_procesar})`}
                </Button>
              </div>
            )}
            {processAllJob && processAllJob.estado === 'procesando' && (
              <div className="flex items-center gap-2">
                <div className="flex items-center gap-2 bg-purple-50 border border-purple-200 rounded-lg px-3 py-1.5">
                  <Loader2 className="h-4 w-4 animate-spin text-purple-600" />
                  <div className="text-xs">
                    <span className="font-medium text-purple-700">{processAllJob.procesados}/{processAllJob.total}</span>
                    <span className="text-purple-500 ml-1">({processAllJob.exitosos} ok, {processAllJob.errores} err)</span>
                  </div>
                </div>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={handleCancelProcessAll}
                  className="text-red-600 hover:bg-red-50 border-red-200"
                  data-testid="cancel-process-all-btn"
                >
                  <XCircle className="h-4 w-4 mr-1" /> Cancelar
                </Button>
              </div>
            )}
            {processAllJob && processAllJob.estado !== 'procesando' && (
              <Badge className={processAllJob.estado === 'completado' ? 'bg-green-100 text-green-700' : processAllJob.estado === 'cancelado' ? 'bg-yellow-100 text-yellow-700' : 'bg-red-100 text-red-700'}>
                {processAllJob.estado === 'completado' ? `Completado: ${processAllJob.exitosos} ok` : processAllJob.estado === 'cancelado' ? `Cancelado: ${processAllJob.exitosos} procesados` : 'Error'}
              </Badge>
            )}
            <Button
              variant="outline"
              size="sm"
              onClick={handleRedownloadAttachments}
              disabled={redownloading}
              className="text-orange-600 hover:bg-orange-50"
              data-testid="redownload-btn"
            >
              {redownloading ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : <Download className="h-4 w-4 mr-1" />}
              {redownloading ? 'Re-descargando...' : 'Re-descargar PDFs'}
            </Button>
            <Button variant="ghost" size="sm" onClick={() => { loadEmails(); loadStats(); }}>
              <RefreshCw className="h-4 w-4" />
            </Button>
            {/* __V62A__ Bulk confirm botons — només a sub-pestanya Pendientes */}
            {emailSubTab === 'pendientes' && (
              <>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => {
                    if (bulkSelectedEmails.size === emails.length && emails.length > 0) bulkClearSelection();
                    else bulkSelectAllVisible(emails.map(e => e.id));
                  }}
                  data-testid="bulk-select-all-btn"
                  className="border-violet-300 text-violet-700 hover:bg-violet-50"
                >
                  {bulkSelectedEmails.size === emails.length && emails.length > 0 ? 'Deseleccionar todos' : `Seleccionar todos (${emails.length})`}
                </Button>
                <Button
                  size="sm"
                  onClick={() => setBulkConfirmDialog(true)}
                  disabled={bulkSelectedEmails.size === 0 || bulkConfirmLoading}
                  data-testid="bulk-confirm-btn"
                  className="bg-emerald-600 hover:bg-emerald-700 text-white"
                >
                  {bulkConfirmLoading ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : <CheckCircle className="h-4 w-4 mr-1" />}
                  Crear actuación{bulkSelectedEmails.size !== 1 ? 'es' : ''}{bulkSelectedEmails.size > 0 ? ` (${bulkSelectedEmails.size})` : ''}
                </Button>
              </>
            )}
          </div>

          {/* Email List */}
          {loading ? (
            <div className="flex items-center justify-center py-16">
              <Loader2 className="h-8 w-8 animate-spin text-gray-400" />
            </div>
          ) : emails.length === 0 ? (
            <Card>
              <CardContent className="flex flex-col items-center justify-center py-16 text-gray-500">
                <Mail className="h-12 w-12 mb-3 text-gray-300" />
                <p className="font-medium">No hay emails</p>
                <p className="text-sm mt-1">Pulse "Descargar Emails" para obtener los emails de procuradores</p>
              </CardContent>
            </Card>
          ) : (
            <div className="space-y-2">
              {/* Select all bar for 'sin procesar' tab */}
              {emailSubTab === 'sin_procesar' && emails.length > 0 && (
                <div className="flex items-center gap-3 px-3 py-2 bg-gray-50 rounded-lg border text-sm">
                  <input
                    type="checkbox"
                    checked={emails.filter(e => e.estado === 'descargado').length > 0 && emails.filter(e => e.estado === 'descargado').every(e => selectedEmails.has(e.id))}
                    onChange={(e) => {
                      const descargados = emails.filter(em => em.estado === 'descargado').map(em => em.id);
                      if (e.target.checked) {
                        setSelectedEmails(new Set([...selectedEmails, ...descargados]));
                      } else {
                        const next = new Set(selectedEmails);
                        descargados.forEach(id => next.delete(id));
                        setSelectedEmails(next);
                      }
                    }}
                    className="h-4 w-4 rounded border-gray-300 text-purple-600 cursor-pointer"
                    data-testid="select-all-checkbox"
                  />
                  <span className="text-gray-600">
                    {selectedEmails.size > 0 ? `${selectedEmails.size} seleccionados` : 'Seleccionar todos'}
                  </span>
                  {selectedEmails.size > 0 && (
                    <Button variant="ghost" size="sm" className="h-6 px-2 text-xs text-gray-500" onClick={() => setSelectedEmails(new Set())} data-testid="clear-selection">
                      Limpiar
                    </Button>
                  )}
                </div>
              )}
              {emails.map((em) => {
                const estadoInfo = ESTADOS[em.estado] || ESTADOS.nuevo;
                const EstadoIcon = estadoInfo.icon;
                return (
                  <Card
                    key={em.id}
                    className={`cursor-pointer transition-all hover:shadow-md ${
                      em.estado === 'pendiente_revision' ? 'border-l-4 border-l-amber-400' :
                      em.estado === 'confirmado' ? 'border-l-4 border-l-green-400' :
                      em.estado === 'error' ? 'border-l-4 border-l-red-400' :
                      em.estado === 'descargado' ? 'border-l-4 border-l-blue-400' : ''
                    }`}
                    data-testid={`email-card-${em.id}`}
                  >
                    <CardContent className="p-3 sm:p-4">
                      <div className="flex items-start gap-3">
                        {emailSubTab === 'sin_procesar' && em.estado === 'descargado' && (
                          <input
                            type="checkbox"
                            checked={selectedEmails.has(em.id)}
                            onChange={(e) => {
                              e.stopPropagation();
                              const next = new Set(selectedEmails);
                              if (e.target.checked) next.add(em.id);
                              else next.delete(em.id);
                              setSelectedEmails(next);
                            }}
                            onClick={(e) => e.stopPropagation()}
                            className="h-4 w-4 mt-1 rounded border-gray-300 text-purple-600 cursor-pointer shrink-0"
                            data-testid={`select-email-${em.id}`}
                          />
                        )}
                        {/* __V62E_CHECKBOX__ */}
                        {emailSubTab === 'pendientes' && em.estado === 'pendiente_revision' && (
                          <input
                            type="checkbox"
                            checked={bulkSelectedEmails.has(em.id)}
                            onChange={(e) => {
                              e.stopPropagation();
                              bulkToggleEmail(em.id);
                            }}
                            onClick={(e) => e.stopPropagation()}
                            className="h-4 w-4 mt-1 rounded border-gray-300 accent-violet-600 cursor-pointer shrink-0"
                            data-testid={`bulk-checkbox-${em.id}`}
                          />
                        )}
                        <div className="flex-1 min-w-0" onClick={() => {
                          // v35: en sub-pestaña "Pendientes" un click directo abre Confirmar
                          if (emailSubTab === 'pendientes' && em.estado === 'pendiente_revision') {
                            openConfirm(em);
                          } else {
                            openDetail(em);
                          }
                        }}>
                          <div className="flex items-center gap-2 mb-1 flex-wrap">
                            <Badge className={`${estadoInfo.color} text-xs`}>
                              <EstadoIcon className="h-3 w-3 mr-1" />
                              {estadoInfo.label}
                            </Badge>
                            {em.tiene_adjuntos && em.adjuntos?.length > 0 && (
                              <Badge
                                variant="outline"
                                className="text-xs cursor-pointer hover:bg-blue-50 hover:text-blue-700 transition-colors"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  const token = localStorage.getItem('lexduo_token');
                                  const pdfAdjuntos = em.adjuntos.filter(a => a.tipo !== 'message/rfc822');
                                  if (pdfAdjuntos.length === 0) return;
                                  if (pdfAdjuntos.length === 1) {
                                    const idx = em.adjuntos.indexOf(pdfAdjuntos[0]);
                                    const url = automatizacionAPI.getPreviewPdfUrl(em.id, idx);
                                    window.open(`${url}?token=${token}`, '_blank');
                                  } else {
                                    pdfAdjuntos.forEach((adj, i) => {
                                      setTimeout(() => {
                                        const idx = em.adjuntos.indexOf(adj);
                                        const url = automatizacionAPI.getPreviewPdfUrl(em.id, idx);
                                        window.open(`${url}?token=${token}`, '_blank');
                                      }, i * 500);
                                    });
                                  }
                                }}
                              >
                                <Paperclip className="h-3 w-3 mr-1" />
                                {em.adjuntos.filter(a => a.tipo !== 'message/rfc822').length} PDF
                                {em.adjuntos.some(a => a.tipo === 'message/rfc822') && ' + EML'}
                              </Badge>
                            )}
                            {em.tiene_adjuntos && (!em.adjuntos || em.adjuntos.length === 0) && (
                              <Badge variant="outline" className="text-xs">
                                <Paperclip className="h-3 w-3 mr-1" />
                                PDF
                              </Badge>
                            )}
                            {em.ia_expediente_numero && (
                              <Badge variant="outline" className="text-xs bg-indigo-50 text-indigo-700">
                                <FolderOpen className="h-3 w-3 mr-1" />
                                {em.ia_expediente_numero}
                              </Badge>
                            )}
                            {em.ia_confianza != null && (
                              <span className={`text-xs ${em.ia_confianza > 0.7 ? 'text-green-600' : em.ia_confianza > 0.4 ? 'text-amber-600' : 'text-red-600'}`}>
                                IA: {Math.round(em.ia_confianza * 100)}%
                              </span>
                            )}
                            {em.es_urgente && (
                              <Badge className="bg-red-600 text-white text-xs animate-pulse">
                                <AlertTriangle className="h-3 w-3 mr-1" />
                                URGENTE
                              </Badge>
                            )}
                            {em.tiene_plazo && !em.es_urgente && (
                              <Badge className="bg-orange-100 text-orange-700 text-xs">
                                <Clock className="h-3 w-3 mr-1" />
                                Con plazo
                              </Badge>
                            )}
                            {em.es_borrador && (
                              <Badge className="bg-sky-100 text-sky-700 text-xs">
                                <Save className="h-3 w-3 mr-1" />
                                Borrador
                              </Badge>
                            )}
                          </div>
                          <h3 className="font-medium text-sm text-gray-900 truncate">{em.asunto || '(Sin asunto)'}</h3>
                          <div className="flex items-center gap-3 mt-1 text-xs text-gray-500">
                            <span className="flex items-center gap-1">
                              <User className="h-3 w-3" />
                              {em.remitente_nombre || em.remitente}
                            </span>
                            <span className="flex items-center gap-1">
                              <Calendar className="h-3 w-3" />
                              {formatDate(em.fecha_email)}
                            </span>
                          </div>
                          {em.ia_titulo && em.estado !== 'confirmado' && (
                            <p className="text-xs text-indigo-600 mt-1 truncate">
                              <Sparkles className="h-3 w-3 inline mr-1" />
                              Propuesta IA: {em.ia_titulo}
                            </p>
                          )}
                          {em.estado === 'confirmado' && em.titulo_confirmado && (
                            <p className="text-xs text-green-600 mt-1 truncate">
                              <CheckCircle className="h-3 w-3 inline mr-1" />
                              Actuación: {em.titulo_confirmado}
                            </p>
                          )}
                        </div>
                        <div className="flex items-center gap-1 shrink-0">
                          {em.estado === 'descargado' && (
                            <Button
                              size="sm"
                              variant="ghost"
                              onClick={(e) => { e.stopPropagation(); handleProcessIA(em.id); }}
                              disabled={processingIA === em.id}
                              title="Procesar con IA"
                              data-testid={`process-ia-${em.id}`}
                            >
                              {processingIA === em.id ?
                                <Loader2 className="h-4 w-4 animate-spin" /> :
                                <Sparkles className="h-4 w-4 text-purple-500" />}
                            </Button>
                          )}
                          {em.estado === 'pendiente_revision' && (
                            <Button
                              size="sm"
                              variant="default"
                              className="bg-green-600 hover:bg-green-700"
                              onClick={(e) => { e.stopPropagation(); openConfirm(em); }}
                              data-testid={`confirm-btn-${em.id}`}
                            >
                              <CheckCircle className="h-4 w-4 mr-1" /> Confirmar
                            </Button>
                          )}
                          {em.estado === 'descartado' && (
                            <>
                              <span className="text-xs text-gray-400 italic mr-1">Desechado</span>
                              <Button
                                size="sm"
                                variant="outline"
                                className="border-zinc-400 text-zinc-700 hover:bg-zinc-100"
                                onClick={(e) => { e.stopPropagation(); handleRestore(em.id); }}
                                title="Recuperar email descartado"
                                data-testid={`restore-list-btn-${em.id}`}
                              >
                                <Undo2 className="h-4 w-4 mr-1" /> Recuperar
                              </Button>
                            </>
                          )}
                          {(em.estado === 'descargado' || em.estado === 'pendiente_revision') && (
                            <Button
                              size="sm"
                              variant="ghost"
                              onClick={(e) => { e.stopPropagation(); setDiscardConfirm(em.id); }}
                              title="Descartar email"
                              data-testid={`discard-list-btn-${em.id}`}
                            >
                              <Trash2 className="h-4 w-4 text-red-400 hover:text-red-600" />
                            </Button>
                          )}
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() => openDetail(em)}
                            data-testid={`detail-btn-${em.id}`}
                          >
                            <Eye className="h-4 w-4" />
                          </Button>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                );
              })}
            </div>
          )}

          {/* Paginación */}
          {totalEmails > PAGE_SIZE && (
            <div className="flex items-center justify-between mt-4 pt-3 border-t" data-testid="pagination">
              <span className="text-sm text-gray-500">
                Mostrando {Math.min((currentPage - 1) * PAGE_SIZE + 1, totalEmails)}-{Math.min(currentPage * PAGE_SIZE, totalEmails)} de {totalEmails} emails
              </span>
              <div className="flex items-center gap-1">
                <Button
                  variant="outline"
                  size="sm"
                  disabled={currentPage <= 1}
                  onClick={() => setCurrentPage(currentPage - 1)}
                  data-testid="prev-page-btn"
                >
                  Anterior
                </Button>
                {Array.from({ length: Math.ceil(totalEmails / PAGE_SIZE) }, (_, i) => i + 1)
                  .filter(p => p === 1 || p === Math.ceil(totalEmails / PAGE_SIZE) || Math.abs(p - currentPage) <= 2)
                  .map((p, idx, arr) => (
                    <React.Fragment key={p}>
                      {idx > 0 && arr[idx - 1] !== p - 1 && (
                        <span className="text-gray-400 text-sm px-1">...</span>
                      )}
                      <Button
                        variant={p === currentPage ? "default" : "outline"}
                        size="sm"
                        className="min-w-[36px]"
                        onClick={() => setCurrentPage(p)}
                      >
                        {p}
                      </Button>
                    </React.Fragment>
                  ))
                }
                <Button
                  variant="outline"
                  size="sm"
                  disabled={currentPage >= Math.ceil(totalEmails / PAGE_SIZE)}
                  onClick={() => setCurrentPage(currentPage + 1)}
                  data-testid="next-page-btn"
                >
                  Siguiente
                </Button>
              </div>
            </div>
          )}
          {totalEmails > 0 && totalEmails <= PAGE_SIZE && (
            <div className="text-sm text-gray-500 mt-3 pt-3 border-t">
              {totalEmails} email{totalEmails !== 1 ? 's' : ''} en total
            </div>
          )}
        </TabsContent>

        <TabsContent value="historial" className="mt-4">
          <HistorialTab loadEmails={loadEmails} formatDate={formatDate} />
        </TabsContent>

        <TabsContent value="reglas" className="mt-4">
          <ReglasTab />
        </TabsContent>
        <TabsContent value="estadisticas" className="mt-4">
          <EstadisticasAutomatizacion />
        </TabsContent>
      </Tabs>

      {/* Detail Dialog */}
      <Dialog open={detailDialog.open} onOpenChange={(v) => !v && setDetailDialog({ open: false, email: null })}>
        <DialogContent className="max-w-7xl max-h-[90vh] overflow-y-auto" style={{ maxWidth: '90vw', width: '90vw', maxHeight: '90vh', height: '90vh' }}>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Mail className="h-5 w-5" /> Detalle del Email
            </DialogTitle>
          </DialogHeader>
          {detailLoading ? (
            <div className="flex items-center justify-center py-8">
              <Loader2 className="h-8 w-8 animate-spin text-gray-400" />
            </div>
          ) : detailDialog.email && (
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-3 text-sm">
                <div>
                  <Label className="text-gray-500">Remitente</Label>
                  <p className="font-medium">{detailDialog.email.remitente_nombre || detailDialog.email.remitente}</p>
                  <p className="text-xs text-gray-400">{detailDialog.email.remitente}</p>
                </div>
                <div>
                  <Label className="text-gray-500">Fecha</Label>
                  <p>{formatDate(detailDialog.email.fecha_email)}</p>
                </div>
              </div>
              <div>
                <Label className="text-gray-500">Asunto</Label>
                <p className="font-medium">{detailDialog.email.asunto}</p>
              </div>

              {detailDialog.email.adjuntos?.length > 0 && (
                <div>
                  <Label className="text-gray-500">Adjuntos</Label>
                  <div className="flex flex-wrap gap-2 mt-1">
                    {detailDialog.email.adjuntos.map((a, i) => {
                      const isEml = a.tipo === 'message/rfc822';
                      return (
                      <div key={i} className="flex items-center gap-1">
                        <Badge variant="outline" className={isEml ? "bg-blue-50" : "bg-red-50"}>
                          {isEml ? <Mail className="h-3 w-3 mr-1 text-blue-500" /> : <FileText className="h-3 w-3 mr-1 text-red-500" />}
                          {a.nombre} ({formatBytes(a.tamano)})
                        </Badge>
                        <Button
                          variant="ghost"
                          size="sm"
                          className="h-7 px-2"
                          onClick={() => {
                            const url = automatizacionAPI.getPreviewPdfUrl(detailDialog.email.id, i);
                            const token = localStorage.getItem('lexduo_token');
                            window.open(`${url}?token=${token}`, '_blank');
                          }}
                          data-testid={`preview-pdf-${i}`}
                        >
                          {isEml ? <Download className="h-3 w-3 text-blue-500" /> : <FileSearch className="h-3 w-3 text-blue-500" />}
                        </Button>
                      </div>
                      );
                    })}
                  </div>
                </div>
              )}

              {detailDialog.email.cuerpo_texto && (
                <div>
                  <Label className="text-gray-500">Contenido del email</Label>
                  <pre className="mt-1 p-3 bg-gray-50 rounded text-xs whitespace-pre-wrap max-h-[50vh] overflow-y-auto">
                    {detailDialog.email.cuerpo_texto}
                  </pre>
                </div>
              )}

              {detailDialog.email.ia_titulo && (
                <Card className="bg-indigo-50 border-indigo-200">
                  <CardContent className="p-3 space-y-2">
                    <h4 className="font-medium text-indigo-800 flex items-center gap-1">
                      <Sparkles className="h-4 w-4" /> Propuesta de la IA
                    </h4>
                    <div className="grid grid-cols-2 gap-2 text-sm">
                      <div>
                        <Label className="text-indigo-600 text-xs">Expediente</Label>
                        <p>{detailDialog.email.ia_expediente_numero || 'No identificado'}</p>
                      </div>
                      <div>
                        <Label className="text-indigo-600 text-xs">Tipo trámite</Label>
                        <p className="capitalize">{detailDialog.email.ia_tipo_tramite || '-'}</p>
                      </div>
                    </div>
                    <div>
                      <Label className="text-indigo-600 text-xs">Título propuesto</Label>
                      <p className="text-sm">{detailDialog.email.ia_titulo}</p>
                    </div>
                    <div>
                      <Label className="text-indigo-600 text-xs">Resumen</Label>
                      <p className="text-sm">{detailDialog.email.ia_resumen}</p>
                    </div>
                    {detailDialog.email.ia_confianza != null && (
                      <div className="text-xs text-indigo-500">
                        Confianza: {Math.round(detailDialog.email.ia_confianza * 100)}
                    <div className="pt-2 border-t border-indigo-200 mt-2"><button data-testid="btn-generar-escrito-ia" onClick={() => setEscritoIADialog({ open: true, email: detailDialog.email })} className="px-3 py-1.5 rounded bg-amber-500 hover:bg-amber-600 text-white text-sm">✨ Generar escrito completo IA</button><p className="text-xs text-indigo-400 mt-1">Redacta el escrito completo (Word) con el estilo del despacho, listo para revisar.</p></div>%
                      </div>
                    )}
                  </CardContent>
                </Card>
              )}

              {detailDialog.email.estado === 'confirmado' && (
                <Card className="bg-green-50 border-green-200">
                  <CardContent className="p-3 space-y-1">
                    <h4 className="font-medium text-green-800 flex items-center gap-1">
                      <CheckCircle className="h-4 w-4" /> Confirmado
                    </h4>
                    <p className="text-sm">Actuación #{detailDialog.email.actuacion_id}</p>
                    <p className="text-sm">{detailDialog.email.titulo_confirmado}</p>
                    <p className="text-xs text-green-600">
                      Por: {detailDialog.email.confirmado_por_nombre} - {formatDate(detailDialog.email.fecha_confirmacion)}
                    </p>
                  </CardContent>
                </Card>
              )}

              {detailDialog.email.error_mensaje && (
                detailDialog.email.estado === 'error' ? (
                  <Card className="bg-red-50 border-red-200">
                    <CardContent className="p-3">
                      <h4 className="font-medium text-red-800 flex items-center gap-1">
                        <AlertCircle className="h-4 w-4" /> Error
                      </h4>
                      <p className="text-sm text-red-600">{detailDialog.email.error_mensaje}</p>
                    </CardContent>
                  </Card>
                ) : (
                  <Card className="bg-amber-50 border-amber-200">
                    <CardContent className="p-3">
                      <h4 className="font-medium text-amber-800 flex items-center gap-1">
                        <AlertCircle className="h-4 w-4" /> Intento anterior fallido
                      </h4>
                      <p className="text-sm text-amber-700">{detailDialog.email.error_mensaje}</p>
                      <p className="text-xs text-amber-600 mt-1">Puedes reintentar el analisis con IA o tramitar el email manualmente.</p>
                    </CardContent>
                  </Card>
                )
              )}

              <DialogFooter className="flex gap-2 flex-wrap">
                {/* Borrar definitivamente - disponible en cualquier estado */}
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => {
                    if (window.confirm('¿Está seguro de que desea BORRAR DEFINITIVAMENTE este email? Esta acción no se puede deshacer y el registro desaparecerá del sistema.')) {
                      handleDelete(detailDialog.email.id);
                    }
                  }}
                  className="text-red-500 hover:text-red-700 hover:bg-red-50 mr-auto"
                  data-testid="delete-email-btn"
                >
                  <Trash2 className="h-4 w-4 mr-1" /> Borrar definitivamente
                </Button>

                {detailDialog.email.estado === 'pendiente_revision' && (
                  <>
                    <Button
                      variant="outline"
                      onClick={() => handleDiscard(detailDialog.email.id)}
                      className="text-amber-600 hover:bg-amber-50"
                      data-testid="discard-email-btn"
                    >
                      <XCircle className="h-4 w-4 mr-1" /> Desechar (no tramitar)
                    </Button>
                    <Button
                      className="bg-green-600 hover:bg-green-700"
                      onClick={() => {
                        setDetailDialog({ open: false, email: null });
                        openConfirm(detailDialog.email);
                      }}
                    >
                      <CheckCircle className="h-4 w-4 mr-1" /> Confirmar Actuación
                    </Button>
                  </>
                )}
                {detailDialog.email.estado === 'descartado' && (
                  <>
                    <span className="text-xs text-gray-500 italic mr-2">Email desechado — consta en el registro pero no se tramitará</span>
                    <Button
                      variant="outline"
                      className="border-zinc-400 text-zinc-700 hover:bg-zinc-100"
                      onClick={() => handleRestore(detailDialog.email.id)}
                      data-testid="restore-email-btn"
                    >
                      <Undo2 className="h-4 w-4 mr-1" /> Recuperar email
                    </Button>
                  </>
                )}
                {(detailDialog.email.estado === 'descargado' || detailDialog.email.estado === 'error') && (
                  <>
                    <Button
                      variant="outline"
                      onClick={() => handleDiscard(detailDialog.email.id)}
                      className="text-amber-600 hover:bg-amber-50"
                    >
                      <XCircle className="h-4 w-4 mr-1" /> Desechar
                    </Button>
                    <Button
                      onClick={() => {
                        handleProcessIA(detailDialog.email.id);
                        setDetailDialog({ open: false, email: null });
                      }}
                    >
                      <Sparkles className="h-4 w-4 mr-1" /> Procesar con IA
                    </Button>
                  </>
                )}
              </DialogFooter>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Confirm Dialog - Fullscreen */}
      <Dialog open={confirmDialog.open} onOpenChange={(v) => !v && setConfirmDialog({ open: false, email: null })}>
        <DialogContent className="overflow-y-auto p-4 sm:p-6" style={{ maxWidth: '98vw', width: '98vw', maxHeight: '98vh', height: '98vh' }}>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-lg">
              <CheckCircle className="h-5 w-5 text-green-600" /> Confirmar y Crear Actuacion
              {confirmForm.es_urgente && (
                <Badge className="bg-red-600 text-white animate-pulse ml-2">
                  <AlertTriangle className="h-3 w-3 mr-1" /> URGENTE
                </Badge>
              )}
              {confirmForm.tiene_plazo && !confirmForm.es_urgente && (
                <Badge className="bg-orange-100 text-orange-700 ml-2">
                  <Clock className="h-3 w-3 mr-1" /> Con plazo
                </Badge>
              )}
            </DialogTitle>
          </DialogHeader>
          {confirmDialog.email && (
            <div className="space-y-4">
              {/* Email info bar */}
              <div className="text-sm text-gray-600 bg-gray-50 p-3 rounded border">
                <div className="flex items-center gap-4 flex-wrap">
                  <span><strong>Email:</strong> {(() => {
                    // v35/v39-fix: destacar en negrita el nº de expediente detectado por IA
                    // dentro del texto del asunto, SIN anteponer prefijos. Si no se encuentra
                    // el codigo completo (ej. "Q-EXP13564-3"), buscamos el nucleo numerico
                    // mas largo (ej. "13564") en el asunto.
                    const subj = confirmDialog.email.asunto || '';
                    const expNum = confirmDialog.email.ia_expediente_numero || '';
                    if (!expNum || !subj) return subj;

                    const buscar = (needle) => {
                      if (!needle) return -1;
                      return subj.toLowerCase().indexOf(needle.toLowerCase());
                    };

                    // 1) Match literal del codigo completo
                    let idx = buscar(expNum);
                    let len = expNum.length;

                    // 2) Sin literal: extraer el numero mas largo del codigo (ej. 13564 de Q-EXP13564-3)
                    if (idx < 0) {
                      const nums = (expNum.match(/\d+/g) || []).sort((a, b) => b.length - a.length);
                      for (const n of nums) {
                        if (n.length >= 3) {  // evitar matchear "3" sueltos
                          // Buscar el nucleo con boundary para evitar falsos positivos
                          const re = new RegExp(`(?<![\\d])${n}(?![\\d])`);
                          const m = re.exec(subj);
                          if (m && m.index >= 0) {
                            idx = m.index;
                            len = n.length;
                            break;
                          }
                        }
                      }
                    }

                    if (idx < 0) {
                      // Sin match: mostrar el asunto tal cual, sin prefijos
                      return subj;
                    }
                    return (<>
                      {subj.slice(0, idx)}
                      <strong className="bg-yellow-100 px-1 rounded">{subj.slice(idx, idx + len)}</strong>
                      {subj.slice(idx + len)}
                    </>);
                  })()}</span>
                  <span className="text-xs text-gray-400">|</span>
                  <span className="text-xs"><User className="h-3 w-3 inline mr-1" />{confirmDialog.email.remitente_nombre || confirmDialog.email.remitente}</span>
                  <span className="text-xs text-gray-400">|</span>
                  <span className="text-xs"><Calendar className="h-3 w-3 inline mr-1" />{formatDate(confirmDialog.email.fecha_email)}</span>
                  {confirmDialog.email.tiene_adjuntos && (
                    <>
                      <span className="text-xs text-gray-400">|</span>
                      <span className="text-xs"><Paperclip className="h-3 w-3 inline mr-1" />{confirmDialog.email.adjuntos?.length || 0} PDF adjuntos</span>
                    </>
                  )}
                </div>
              </div>

              {/* Two-column layout */}
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                {/* Left column - Form */}
                <div className="space-y-4">
                  {/* Expediente selector */}
                  <div>
                    <Label className="font-semibold">Expediente *</Label>
                    {selectedExp ? (
                      <div className="flex items-center gap-3 mt-1 flex-wrap">
                        <button
                          type="button"
                          onClick={() => window.open(`/expedientes/${selectedExp.id}`, '_blank', 'noopener')}
                          title="Abrir el expediente en una ventana nueva"
                          className="inline-flex items-center gap-2 px-4 py-2 rounded-md bg-indigo-100 text-indigo-800 hover:bg-indigo-200 hover:underline border border-indigo-300 transition-colors"
                          data-testid="confirm-expediente-link"
                        >
                          <FolderOpen className="h-6 w-6" />
                          <span className="text-2xl font-bold tracking-wide">{selectedExp.numero_expediente}</span>
                          <ExternalLink className="h-4 w-4 opacity-60" />
                        </button>
                        <span className="text-sm text-gray-600">{selectedExp.titulo}</span>
                        <Button variant="ghost" size="sm" onClick={() => { setSelectedExp(null); setConfirmForm({...confirmForm, expediente_id: ''}); }} title="Quitar expediente">
                          <XCircle className="h-4 w-4 text-gray-400" />
                        </Button>
                      </div>
                    ) : (
                      <div className="mt-1">
                        <Input
                          placeholder="Buscar expediente por numero o titulo..."
                          value={expSearch}
                          onChange={(e) => searchExpedientes(e.target.value)}
                          data-testid="exp-search"
                        />
                        {expResults.length > 0 && (
                          <div className="mt-1 border rounded max-h-40 overflow-y-auto">
                            {expResults.map((exp) => (
                              <button
                                key={exp.id}
                                className="w-full text-left px-3 py-2 hover:bg-gray-100 text-sm border-b last:border-b-0"
                                onClick={() => {
                                  setSelectedExp(exp);
                                  setConfirmForm({...confirmForm, expediente_id: exp.id});
                                  setExpResults([]);
                                  setExpSearch('');
                                }}
                              >
                                <span className="font-medium text-indigo-700">{exp.numero_expediente}</span>
                                {exp.titulo && <span className="text-gray-500 ml-2">{exp.titulo}</span>}
                              </button>
                            ))}
                          </div>
                        )}
                      </div>
                    )}
                  </div>

                  <div>
                    <Label className="font-semibold">Titulo de la actuacion * <span className="text-xs text-gray-400 font-normal">(MAYUSCULAS + N.Int usuario)</span></Label>
                    <Textarea
                      value={confirmForm.titulo}
                      onChange={(e) => setConfirmForm({...confirmForm, titulo: e.target.value})}
                      className="uppercase font-medium resize-none"
                      rows={2}
                      data-testid="confirm-titulo"
                    />
                  </div>

                  <div>
                    <Label className="font-semibold">Tipo de tramite</Label>
                    <Select value={confirmForm.tipo_tramite} onValueChange={(v) => setConfirmForm({...confirmForm, tipo_tramite: v})}>
                      <SelectTrigger data-testid="confirm-tipo">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {TIPOS_TRAMITE.map((t) => (
                          <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>

                  <div>
                    <Label className="font-semibold">Resumen</Label>
                    <Textarea
                      value={confirmForm.resumen}
                      onChange={(e) => setConfirmForm({...confirmForm, resumen: e.target.value})}
                      rows={4}
                      data-testid="confirm-resumen"
                    />
                  </div>
                </div>

                {/* Right column - v38: Tabs "Avisos" / "Actuaciones" */}
                <div className="space-y-2">
                  <Tabs value={rightPanelTab} onValueChange={setRightPanelTab} className="w-full">
                    <TabsList className="grid grid-cols-2 w-full">
                      <TabsTrigger value="avisos" data-testid="right-tab-avisos" className="flex items-center gap-2">
                        <AlertTriangle className="h-4 w-4" />
                        Avisos
                      </TabsTrigger>
                      <TabsTrigger value="actuaciones" data-testid="right-tab-actuaciones" className="flex items-center gap-2" disabled={!selectedExp}>
                        <List className="h-4 w-4" />
                        Actuaciones
                        {actuacionesPrevias.length > 0 && (
                          <Badge variant="outline" className="text-[10px] ml-1 font-normal">
                            {actuacionesPrevias.length}
                          </Badge>
                        )}
                      </TabsTrigger>
                    </TabsList>

                    <TabsContent value="avisos" className="space-y-4 mt-3">
                  {/* Plazo / Urgencia section */}
                  <Card className={`${confirmForm.es_urgente ? 'border-red-400 bg-red-50' : confirmForm.tiene_plazo ? 'border-orange-300 bg-orange-50' : 'border-gray-200'}`}>
                    <CardContent className="p-4 space-y-3">
                      <h4 className="font-semibold text-sm flex items-center gap-2">
                        <AlertTriangle className={`h-4 w-4 ${confirmForm.es_urgente ? 'text-red-600' : 'text-orange-500'}`} />
                        Plazos y Urgencia
                      </h4>
                      <div className="flex items-center gap-4">
                        <label className="flex items-center gap-2 cursor-pointer">
                          <input
                            type="checkbox"
                            checked={confirmForm.tiene_plazo}
                            onChange={(e) => setConfirmForm({...confirmForm, tiene_plazo: e.target.checked, es_urgente: !e.target.checked ? false : confirmForm.es_urgente})}
                            data-testid="confirm-tiene-plazo"
                          />
                          <span className="text-sm">Tiene plazo / diligencia</span>
                        </label>
                        <label className={`flex items-center gap-2 cursor-pointer ${!confirmForm.tiene_plazo ? 'opacity-40' : ''}`}>
                          <input
                            type="checkbox"
                            checked={confirmForm.es_urgente}
                            onChange={(e) => setConfirmForm({...confirmForm, es_urgente: e.target.checked})}
                            disabled={!confirmForm.tiene_plazo}
                            data-testid="confirm-es-urgente"
                          />
                          <span className="text-sm font-medium text-red-600">URGENTE</span>
                        </label>
                      </div>
                      {confirmForm.tiene_plazo && (
                        <div>
                          <Label className="text-sm">Fecha limite</Label>
                          <Input
                            type="date"
                            value={confirmForm.fecha_limite}
                            onChange={(e) => setConfirmForm({...confirmForm, fecha_limite: e.target.value})}
                            className="w-48"
                            data-testid="confirm-fecha-limite"
                          />
                        </div>
                      )}
                    </CardContent>
                  </Card>

                  {/* Tramites que requieren notificar al cliente: sentencia, recurso_apelacion_banco, senalamiento */}
                  {(() => {
                    const tipo = (confirmForm.tipo_tramite || '').toLowerCase();
                    if (tipo === 'sentencia') {
                      return (
                        <Card className="border-purple-300 bg-purple-50">
                          <CardContent className="p-4 space-y-2">
                            <h4 className="font-semibold text-sm flex items-center gap-2 text-purple-800">
                              <FileText className="h-4 w-4" />
                              Sentencia detectada
                            </h4>
                            <p className="text-xs text-purple-600">
                              Las sentencias pueden requerir notificacion al cliente. Desmarque la opcion si la IA se ha equivocado en la clasificacion.
                            </p>
                            <label className="flex items-center gap-2 cursor-pointer pt-1">
                              <input
                                type="checkbox"
                                checked={confirmForm.notificar_cliente}
                                onChange={(e) => setConfirmForm({...confirmForm, notificar_cliente: e.target.checked})}
                                data-testid="confirm-notificar-cliente"
                              />
                              <span className="text-sm font-medium text-purple-800">Generar borrador de email al cliente</span>
                            </label>
                            {!confirmForm.notificar_cliente && (
                              <p className="text-xs text-amber-600 font-medium">No se generara borrador de email al confirmar esta actuacion.</p>
                            )}
                          </CardContent>
                        </Card>
                      );
                    }
                    if (tipo === 'recurso_apelacion_banco') {
                      return (
                        <Card className="border-orange-300 bg-orange-50">
                          <CardContent className="p-4 space-y-2">
                            <h4 className="font-semibold text-sm flex items-center gap-2 text-orange-800">
                              <AlertTriangle className="h-4 w-4" />
                              Recurs d'apel·lacio del banc detectat
                            </h4>
                            <p className="text-xs text-orange-700">
                              La IA ha identificat que la part contraria ha presentat recurs d'apel·lacio. Es generara un esborrany d'email al client informant-lo. Desmarqui si la IA s'ha equivocat.
                            </p>
                            <label className="flex items-center gap-2 cursor-pointer pt-1">
                              <input
                                type="checkbox"
                                checked={confirmForm.notificar_cliente}
                                onChange={(e) => setConfirmForm({...confirmForm, notificar_cliente: e.target.checked})}
                                data-testid="confirm-notificar-recurso"
                              />
                              <span className="text-sm font-medium text-orange-800">Generar esborrany d'email al client (recurs apel·lacio)</span>
                            </label>
                            {!confirmForm.notificar_cliente && (
                              <p className="text-xs text-amber-600 font-medium">No se generara borrador de email al confirmar esta actuacion.</p>
                            )}
                          </CardContent>
                        </Card>
                      );
                    }
                    if (tipo === 'senalamiento') {
                      return (
                        <Card className="border-teal-300 bg-teal-50">
                          <CardContent className="p-4 space-y-2">
                            <h4 className="font-semibold text-sm flex items-center gap-2 text-teal-800">
                              <Calendar className="h-4 w-4" />
                              Assenyalament judicial detectat
                            </h4>
                            <p className="text-xs text-teal-700">
                              La IA ha identificat un assenyalament (vista/audiencia previa/judici). El borrador inclou la data del senyalament i el jutjat de l'expedient. Desmarqui si la IA s'ha equivocat.
                            </p>
                            {confirmForm.fecha_limite && (
                              <p className="text-xs text-teal-800 font-medium">Data detectada: <strong>{confirmForm.fecha_limite}</strong></p>
                            )}
                            <label className="flex items-center gap-2 cursor-pointer pt-1">
                              <input
                                type="checkbox"
                                checked={confirmForm.notificar_cliente}
                                onChange={(e) => setConfirmForm({...confirmForm, notificar_cliente: e.target.checked})}
                                data-testid="confirm-notificar-senalamiento"
                              />
                              <span className="text-sm font-medium text-teal-800">Generar esborrany d'email al client (assenyalament)</span>
                            </label>
                            {!confirmForm.notificar_cliente && (
                              <p className="text-xs text-amber-600 font-medium">No se generara borrador de email al confirmar esta actuacion.</p>
                            )}
                          </CardContent>
                        </Card>
                      );
                    }
                    return null;
                  })()}

                  {/* IA Proposal card */}
                  {confirmDialog.email.ia_titulo && (
                    <Card className="bg-indigo-50 border-indigo-200">
                      <CardContent className="p-4 space-y-2">
                        <h4 className="font-medium text-indigo-800 flex items-center gap-1 text-sm">
                          <Sparkles className="h-4 w-4" /> Propuesta de la IA
                        </h4>
                        <div className="grid grid-cols-2 gap-2 text-xs">
                          <div>
                            <span className="text-indigo-600">Expediente:</span>
                            <span className="ml-1">{confirmDialog.email.ia_expediente_numero || 'No identificado'}</span>
                          </div>
                          <div>
                            <span className="text-indigo-600">Tipo:</span>
                            <span className="ml-1 capitalize">{confirmDialog.email.ia_tipo_tramite || '-'}</span>
                          </div>
                          <div className="col-span-2">
                            <span className="text-indigo-600">Titulo:</span>
                            <span className="ml-1">{confirmDialog.email.ia_titulo}</span>
                          </div>
                          {confirmDialog.email.ia_confianza != null && (
                            <div className="col-span-2">
                              <span className="text-indigo-600">Confianza:</span>
                              <span className="ml-1">{Math.round(confirmDialog.email.ia_confianza * 100)}%</span>
                            </div>
                          )}
                        </div>
                      </CardContent>
                    </Card>
                  )}

                  {/* IA Feedback indicator - muestra campos modificados respecto a la IA */}
                  {confirmDialog.email.ia_titulo && (() => {
                    const em = confirmDialog.email;
                    const cambios = [];
                    if (em.ia_titulo && confirmForm.titulo && em.ia_titulo.trim().toUpperCase() !== confirmForm.titulo.trim().toUpperCase()) cambios.push('titulo');
                    if (em.ia_expediente_id && confirmForm.expediente_id && String(em.ia_expediente_id) !== String(confirmForm.expediente_id)) cambios.push('expediente');
                    if (em.ia_tipo_tramite && confirmForm.tipo_tramite && em.ia_tipo_tramite.toLowerCase() !== confirmForm.tipo_tramite.toLowerCase()) cambios.push('tipo de tramite');
                    const iaPlazo = em.tiene_plazo || false;
                    if (iaPlazo !== (confirmForm.tiene_plazo || false)) cambios.push('plazo');
                    if (cambios.length === 0) return null;
                    return (
                      <Card className="bg-amber-50 border-amber-300" data-testid="ia-feedback-indicator">
                        <CardContent className="p-3">
                          <div className="flex items-start gap-2">
                            <Sparkles className="h-4 w-4 text-amber-600 mt-0.5 shrink-0" />
                            <div>
                              <p className="text-xs font-semibold text-amber-800">Correcciones detectadas sobre la IA</p>
                              <p className="text-xs text-amber-700 mt-0.5">
                                Has modificado: <strong>{cambios.join(', ')}</strong>. Al confirmar, la IA registrara esta correccion para mejorar en futuros analisis similares.
                              </p>
                            </div>
                          </div>
                        </CardContent>
                      </Card>
                    );
                  })()}

                  {/* Adjuntos - clicables para previsualizar embebido abajo */}
                  {confirmDialog.email.adjuntos?.length > 0 && (
                    <div className="text-sm bg-gray-50 p-3 rounded border space-y-2">
                      <div className="flex items-center gap-1.5 text-gray-600">
                        <Paperclip className="h-4 w-4" />
                        Se adjuntaran <strong className="mx-0.5">{confirmDialog.email.adjuntos.length}</strong> archivo(s) PDF a la actuacion
                      </div>
                      <div className="flex flex-wrap gap-2">
                        {confirmDialog.email.adjuntos.map((a, i) => (
                          <Button
                            key={i}
                            variant={previewPdfIdx === i ? 'default' : 'outline'}
                            size="sm"
                            className={`h-8 text-xs ${previewPdfIdx === i ? 'bg-red-600 hover:bg-red-700 text-white' : 'bg-white hover:bg-blue-50 border-gray-300'}`}
                            onClick={() => selectPreviewPdf(confirmDialog.email.id, i)}
                            title="Mostrar este PDF en el visor inferior"
                            data-testid={`confirm-preview-pdf-${i}`}
                          >
                            <FileSearch className="h-3.5 w-3.5 mr-1.5" />
                            {a.nombre}
                            {a.tamano ? ` (${formatBytes(a.tamano)})` : ''}
                          </Button>
                        ))}
                        <Button
                          variant="ghost"
                          size="sm"
                          className="h-8 text-xs text-gray-500 hover:text-gray-700"
                          onClick={() => {
                            if (previewPdfUrl) window.open(previewPdfUrl, '_blank');
                          }}
                          disabled={!previewPdfUrl}
                          title="Abrir en pestana nueva"
                          data-testid="confirm-preview-pdf-newtab"
                        >
                          <ExternalLink className="h-3.5 w-3.5 mr-1" /> Pestana nueva
                        </Button>
                      </div>
                    </div>
                  )}
                    </TabsContent>

                    {/* v38: TAB ACTUACIONES — lista de actuaciones previas del expediente */}
                    <TabsContent value="actuaciones" className="mt-3" data-testid="tab-actuaciones-content">
                      {!selectedExp ? (
                        <Card className="border-dashed">
                          <CardContent className="p-6 text-center text-sm text-gray-500">
                            <FolderOpen className="h-8 w-8 mx-auto mb-2 text-gray-300" />
                            Seleccione un expediente para ver sus actuaciones previas.
                          </CardContent>
                        </Card>
                      ) : loadingActuaciones ? (
                        <div className="py-8 flex justify-center">
                          <Loader2 className="h-6 w-6 animate-spin text-gray-400" />
                        </div>
                      ) : actuacionesPrevias.length === 0 ? (
                        <Card className="border-dashed">
                          <CardContent className="p-6 text-center text-sm text-gray-500">
                            <List className="h-8 w-8 mx-auto mb-2 text-gray-300" />
                            Este expediente no tiene actuaciones previas.
                          </CardContent>
                        </Card>
                      ) : (
                        <div className="space-y-2 max-h-[55vh] overflow-y-auto pr-1" data-testid="actuaciones-list">
                          {actuacionesPrevias.map((act) => {
                            const isExpanded = expandedActuacionId === act.id;
                            const adjuntosAct = adjuntosPorActuacion[act.id] || [];
                            return (
                              <Card key={act.id} className={`transition-colors ${isExpanded ? 'border-blue-300 shadow-sm' : 'hover:bg-gray-50'}`} data-testid={`actuacion-card-${act.id}`}>
                                <button
                                  type="button"
                                  onClick={() => toggleExpandActuacion(act.id)}
                                  className="w-full text-left p-3 flex items-start gap-2"
                                >
                                  <div className="flex-shrink-0 mt-0.5">
                                    {isExpanded ? <ChevronUp className="h-4 w-4 text-blue-600" /> : <ChevronDown className="h-4 w-4 text-gray-400" />}
                                  </div>
                                  <div className="flex-1 min-w-0">
                                    <div className="flex items-center gap-2 flex-wrap">
                                      <Badge variant="outline" className="text-[10px] uppercase">{act.tipo || 'tramite'}</Badge>
                                      <span className="text-xs text-gray-500">{formatDate(act.fecha)}</span>
                                    </div>
                                    <p className="text-sm font-medium text-gray-800 mt-1 line-clamp-1">
                                      {act.titulo || '(Sin titulo)'}
                                    </p>
                                    {!isExpanded && act.descripcion && (
                                      <p className="text-xs text-gray-500 mt-0.5 line-clamp-1">
                                        {act.descripcion.slice(0, 120)}
                                      </p>
                                    )}
                                  </div>
                                </button>

                                {isExpanded && (
                                  <div className="border-t bg-gray-50/50 px-3 py-3 space-y-3">
                                    {act.descripcion && (
                                      <div>
                                        <p className="text-[11px] font-semibold text-gray-500 uppercase mb-1">Descripcion</p>
                                        <pre className="text-xs text-gray-700 whitespace-pre-wrap font-sans leading-relaxed">{act.descripcion}</pre>
                                      </div>
                                    )}
                                    {act.observaciones && (
                                      <div>
                                        <p className="text-[11px] font-semibold text-gray-500 uppercase mb-1">Observaciones</p>
                                        <p className="text-xs text-gray-700">{act.observaciones}</p>
                                      </div>
                                    )}
                                    {/* Adjuntos */}
                                    <div>
                                      <p className="text-[11px] font-semibold text-gray-500 uppercase mb-1.5">
                                        Adjuntos {adjuntosAct.length > 0 && <span className="font-normal text-gray-400">({adjuntosAct.length})</span>}
                                      </p>
                                      {adjuntosAct.length === 0 ? (
                                        <p className="text-xs text-gray-400 italic">Sin adjuntos.</p>
                                      ) : (
                                        <div className="flex flex-wrap gap-1.5">
                                          {adjuntosAct.map((adj) => {
                                            const active = actuacionPreview.adjuntoId === adj.id;
                                            return (
                                              <Button
                                                key={adj.id}
                                                variant={active ? 'default' : 'outline'}
                                                size="sm"
                                                className={`h-7 text-[11px] ${active ? 'bg-blue-600 hover:bg-blue-700 text-white' : 'bg-white'}`}
                                                onClick={() => previewAdjuntoActuacion(adj)}
                                                data-testid={`actuacion-${act.id}-adj-${adj.id}`}
                                              >
                                                <FileSearch className="h-3 w-3 mr-1" />
                                                {adj.nombre}
                                              </Button>
                                            );
                                          })}
                                        </div>
                                      )}
                                    </div>

                                    {/* Preview inline del adjunto seleccionado */}
                                    {actuacionPreview.adjuntoId && adjuntosAct.some(a => a.id === actuacionPreview.adjuntoId) && (
                                      <div className="border rounded-lg bg-white" data-testid="actuacion-preview-iframe-wrap">
                                        <div className="px-2.5 py-1.5 bg-gray-100 border-b text-[11px] text-gray-700 flex items-center justify-between">
                                          <span className="truncate">
                                            <FileText className="h-3 w-3 inline mr-1 text-red-600" />
                                            {actuacionPreview.nombre}
                                          </span>
                                          <Button
                                            variant="ghost"
                                            size="sm"
                                            className="h-6 text-[11px] px-2"
                                            onClick={() => window.open(actuacionPreview.url, '_blank')}
                                            title="Abrir en pestana nueva"
                                          >
                                            <ExternalLink className="h-3 w-3" />
                                          </Button>
                                        </div>
                                        <iframe
                                          src={actuacionPreview.url}
                                          title={actuacionPreview.nombre}
                                          className="w-full bg-white"
                                          style={{ height: '40vh', border: 'none' }}
                                        />
                                      </div>
                                    )}
                                  </div>
                                )}
                              </Card>
                            );
                          })}
                        </div>
                      )}
                    </TabsContent>
                  </Tabs>
                </div>
              </div>

              {/* Footer with all buttons - antes del visor PDF, pegajoso para estar siempre visible */}
              <DialogFooter className="flex flex-wrap gap-2 border-t border-b pt-4 pb-4 bg-white sticky top-0 z-10 -mx-4 sm:-mx-6 px-4 sm:px-6">
                <div className="flex items-center gap-2 mr-auto">
                  <Button
                    variant="outline"
                    onClick={handleReanalyze}
                    disabled={reanalyzing || confirming}
                    className="text-purple-600 hover:bg-purple-50"
                    data-testid="reanalyze-btn"
                  >
                    {reanalyzing ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : <RotateCcw className="h-4 w-4 mr-1" />}
                    Volver a analizar
                  </Button>
                </div>
                <Button variant="outline" onClick={() => setConfirmDialog({ open: false, email: null })} data-testid="confirm-cancel-btn">
                  Cancelar
                </Button>
                <Button
                  variant="outline"
                  onClick={handleSaveDraft}
                  disabled={savingDraft || confirming}
                  className="text-sky-600 hover:bg-sky-50"
                  data-testid="save-draft-btn"
                >
                  {savingDraft ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : <Save className="h-4 w-4 mr-1" />}
                  Guardar
                </Button>
                <Button
                  className="bg-green-600 hover:bg-green-700"
                  onClick={handleConfirm}
                  disabled={confirming || savingDraft}
                  data-testid="confirm-submit-btn"
                >
                  {confirming ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : <CheckCircle className="h-4 w-4 mr-1" />}
                  Crear Actuacion
                </Button>
              </DialogFooter>

              {/* Embedded PDF preview - lower section, scrollable */}
              {confirmDialog.email.adjuntos?.length > 0 && previewPdfUrl && (
                <div className="border-2 border-gray-200 rounded-lg bg-gray-100" data-testid="confirm-pdf-viewer">
                  <div className="px-3 py-2 bg-gray-200 border-b text-xs text-gray-700 font-medium flex items-center justify-between">
                    <span>
                      <FileText className="h-3.5 w-3.5 inline mr-1 text-red-600" />
                      Previsualizacion: {confirmDialog.email.adjuntos[previewPdfIdx]?.nombre || ''}
                    </span>
                    <span className="text-gray-500">Scroll para leer el documento sin perder el formulario de arriba</span>
                  </div>
                  <iframe
                    src={previewPdfUrl}
                    title="Previsualizacion PDF"
                    className="w-full bg-white"
                    style={{ height: '55vh', border: 'none' }}
                  />
                </div>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Fetch Dialog - Filtro de descarga */}
      <Dialog open={fetchDialog} onOpenChange={setFetchDialog}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Download className="h-5 w-5 text-blue-600" /> Descargar Emails
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <p className="text-sm text-gray-600">
              Seleccione el rango de fechas para descargar solo los emails de ese período.
            </p>

            <div className="flex gap-2">
              <Button
                variant="outline"
                size="sm"
                onClick={() => {
                  const hoy = new Date().toISOString().split('T')[0];
                  setFetchForm({...fetchForm, fecha_desde: hoy, fecha_hasta: hoy});
                }}
                data-testid="fetch-hoy-btn"
              >
                <Calendar className="h-3 w-3 mr-1" /> Hoy
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={() => {
                  const hoy = new Date();
                  const ayer = new Date(hoy);
                  ayer.setDate(ayer.getDate() - 1);
                  setFetchForm({...fetchForm, fecha_desde: ayer.toISOString().split('T')[0], fecha_hasta: hoy.toISOString().split('T')[0]});
                }}
              >
                Ayer + Hoy
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={() => {
                  const hoy = new Date();
                  const semana = new Date(hoy);
                  semana.setDate(semana.getDate() - 7);
                  setFetchForm({...fetchForm, fecha_desde: semana.toISOString().split('T')[0], fecha_hasta: hoy.toISOString().split('T')[0]});
                }}
              >
                Última semana
              </Button>
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>Fecha desde *</Label>
                <Input
                  type="date"
                  value={fetchForm.fecha_desde}
                  onChange={(e) => setFetchForm({...fetchForm, fecha_desde: e.target.value})}
                  data-testid="fetch-fecha-desde"
                />
              </div>
              <div>
                <Label>Fecha hasta *</Label>
                <Input
                  type="date"
                  value={fetchForm.fecha_hasta}
                  onChange={(e) => setFetchForm({...fetchForm, fecha_hasta: e.target.value})}
                  data-testid="fetch-fecha-hasta"
                />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>Hora desde <span className="text-xs text-gray-400">(opcional)</span></Label>
                <Input
                  type="time"
                  value={fetchForm.hora_desde}
                  onChange={(e) => setFetchForm({...fetchForm, hora_desde: e.target.value})}
                  data-testid="fetch-hora-desde"
                />
              </div>
              <div>
                <Label>Hora hasta <span className="text-xs text-gray-400">(opcional)</span></Label>
                <Input
                  type="time"
                  value={fetchForm.hora_hasta}
                  onChange={(e) => setFetchForm({...fetchForm, hora_hasta: e.target.value})}
                  data-testid="fetch-hora-hasta"
                />
              </div>
            </div>

            <div>
              <Label>Filtrar por remitente <span className="text-xs text-gray-400">(opcional)</span></Label>
              <Input
                placeholder="ej: procurador.garcia@dominio.com"
                value={fetchForm.remitente || ''}
                onChange={(e) => setFetchForm({...fetchForm, remitente: e.target.value})}
                data-testid="fetch-remitente"
              />
            </div>

            <div>
              <Label>Límite de emails a descargar</Label>
              <Input
                type="number"
                value={fetchForm.limit}
                onChange={(e) => setFetchForm({...fetchForm, limit: parseInt(e.target.value) || 50})}
                min={1}
                max={500}
                data-testid="fetch-limit"
              />
            </div>

            <div className="flex items-center gap-2 text-xs text-gray-500 bg-gray-50 p-2 rounded">
              <AlertCircle className="h-4 w-4 shrink-0" />
              <span>Los emails ya descargados se detectarán como duplicados y no se importarán de nuevo.</span>
            </div>

            <DialogFooter>
              <Button variant="outline" onClick={() => setFetchDialog(false)}>Cancelar</Button>
              <Button onClick={executeFetch} disabled={fetching} data-testid="execute-fetch-btn">
                {fetching ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : <Download className="h-4 w-4 mr-1" />}
                Descargar
              </Button>
            </DialogFooter>
          </div>
        </DialogContent>
      </Dialog>

      {/* Config Dialog */}
      <Dialog open={configDialog} onOpenChange={setConfigDialog}>
        <DialogContent className="max-w-lg max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Settings className="h-5 w-5" /> Configuración - Emails Procuradores
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label>Nombre de la cuenta</Label>
              <Input
                value={configForm.nombre}
                onChange={(e) => setConfigForm({...configForm, nombre: e.target.value})}
                placeholder="Ej: Procuradores"
                data-testid="config-nombre"
              />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>Servidor IMAP *</Label>
                <Input
                  value={configForm.imap_host}
                  onChange={(e) => setConfigForm({...configForm, imap_host: e.target.value})}
                  placeholder="mail.lexduo.cat"
                  data-testid="config-imap-host"
                />
              </div>
              <div>
                <Label>Puerto</Label>
                <Input
                  type="number"
                  value={configForm.imap_port}
                  onChange={(e) => setConfigForm({...configForm, imap_port: parseInt(e.target.value) || 993})}
                  data-testid="config-imap-port"
                />
              </div>
            </div>
            <div>
              <Label>Usuario (email) *</Label>
              <Input
                value={configForm.imap_user}
                onChange={(e) => setConfigForm({...configForm, imap_user: e.target.value})}
                placeholder="procuradors@lexduo.cat"
                data-testid="config-imap-user"
              />
            </div>
            <div>
              <Label>Contraseña *</Label>
              <Input
                type="password"
                value={configForm.imap_password}
                onChange={(e) => setConfigForm({...configForm, imap_password: e.target.value})}
                placeholder={config ? '(sin cambios si vacío)' : 'Contraseña del email'}
                data-testid="config-imap-password"
              />
            </div>
            <div>
              <Label>Carpeta IMAP</Label>
              <Input
                value={configForm.imap_carpeta}
                onChange={(e) => setConfigForm({...configForm, imap_carpeta: e.target.value})}
                placeholder="INBOX"
                data-testid="config-imap-carpeta"
              />
            </div>
            <div className="flex items-center gap-2">
              <input
                type="checkbox"
                id="imap_ssl"
                checked={configForm.imap_use_ssl}
                onChange={(e) => setConfigForm({...configForm, imap_use_ssl: e.target.checked})}
              />
              <Label htmlFor="imap_ssl">Usar SSL</Label>
            </div>
            <div className="flex items-center gap-2">
              <input
                type="checkbox"
                id="auto_process"
                checked={configForm.auto_process_ia}
                onChange={(e) => setConfigForm({...configForm, auto_process_ia: e.target.checked})}
              />
              <Label htmlFor="auto_process">Procesar automáticamente con IA al descargar</Label>
            </div>
            <p className="text-xs text-gray-400 -mt-2 ml-6">
              Si se activa, tras descargar emails se lanzará automáticamente el análisis IA. Recomendamos dejarlo desactivado hasta verificar la fiabilidad del análisis.
            </p>

            <div className="border-t pt-3 mt-3">
              <h4 className="text-sm font-medium text-gray-700 mb-2">Descarga automática programada</h4>
              <div className="flex items-center gap-2">
                <input
                  type="checkbox"
                  id="auto_fetch"
                  checked={configForm.auto_fetch}
                  onChange={(e) => setConfigForm({...configForm, auto_fetch: e.target.checked})}
                />
                <Label htmlFor="auto_fetch">Activar descarga automática periódica</Label>
              </div>
              {configForm.auto_fetch && (
                <div className="mt-2 ml-6">
                  <Label>Intervalo (minutos)</Label>
                  <Input
                    type="number"
                    className="w-32"
                    value={configForm.auto_fetch_intervalo_min}
                    onChange={(e) => setConfigForm({...configForm, auto_fetch_intervalo_min: parseInt(e.target.value) || 15})}
                    min={5}
                    max={1440}
                  />
                  <p className="text-xs text-gray-400 mt-1">El sistema descargará emails automáticamente cada {configForm.auto_fetch_intervalo_min} minutos</p>
                </div>
              )}
            </div>

            <DialogFooter className="flex gap-2">
              <Button
                variant="outline"
                onClick={handleTestConnection}
                disabled={testingConnection}
                data-testid="test-connection-btn"
              >
                {testingConnection ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : <RefreshCw className="h-4 w-4 mr-1" />}
                Probar Conexión
              </Button>
              <Button
                onClick={handleSaveConfig}
                disabled={savingConfig}
                data-testid="save-config-btn"
              >
                {savingConfig ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : null}
                Guardar
              </Button>
            </DialogFooter>
          </div>
        </DialogContent>
      </Dialog>

      {/* Email Composer Dialog - Post-confirmation for Sentencias */}
      <Dialog open={emailComposer.open} onOpenChange={(v) => { if (!v) handleEmailSaveLater(); }}>
        <DialogContent className="max-w-3xl max-h-[85vh] overflow-y-auto" data-testid="email-composer-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-lg">
              <Send className="h-5 w-5 text-purple-600" />
              Enviar Email al Cliente — Sentencia
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            {/* Info bar */}
            <div className="text-sm bg-purple-50 border border-purple-200 rounded p-3 flex items-center gap-3">
              <FileText className="h-5 w-5 text-purple-600 shrink-0" />
              <div>
                <p className="font-medium text-purple-800">
                  Borrador generado automáticamente (Consulta #{emailComposer.consulta_id})
                </p>
                <p className="text-xs text-purple-600">
                  Expediente: {emailComposer.expediente_numero} — Revise el contenido antes de enviar
                </p>
              </div>
            </div>

            {/* Template selector */}
            {emailPlantillas.length > 0 && (
              <div>
                <Label className="font-semibold">Usar plantilla</Label>
                <Select onValueChange={handleEmailPlantillaSelect}>
                  <SelectTrigger data-testid="email-composer-plantilla">
                    <SelectValue placeholder="Seleccionar plantilla (opcional)" />
                  </SelectTrigger>
                  <SelectContent>
                    {emailPlantillas.map((p) => (
                      <SelectItem key={p.id} value={p.id.toString()}>{p.nombre}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}

            {/* Recipient */}
            <div>
              <Label className="font-semibold">Destinatario</Label>
              <div className="flex items-center gap-2 mt-1">
                <Input
                  type="email"
                  value={emailComposer.destinatario_email}
                  onChange={(e) => setEmailComposer(prev => ({ ...prev, destinatario_email: e.target.value }))}
                  placeholder="email@cliente.com"
                  data-testid="email-composer-destinatario"
                />
                {emailComposer.destinatario_nombre && (
                  <Badge variant="outline" className="shrink-0 text-xs">
                    <User className="h-3 w-3 mr-1" />
                    {emailComposer.destinatario_nombre}
                  </Badge>
                )}
              </div>
              {!emailComposer.destinatario_email && (
                <p className="text-xs text-amber-600 mt-1">
                  <AlertCircle className="h-3 w-3 inline mr-1" />
                  El cliente no tiene email registrado. Introduzca uno manualmente.
                </p>
              )}
            </div>

            {/* Subject */}
            <div>
              <Label className="font-semibold">Asunto</Label>
              <Input
                value={emailComposer.asunto}
                onChange={(e) => setEmailComposer(prev => ({ ...prev, asunto: e.target.value }))}
                data-testid="email-composer-asunto"
              />
            </div>

            {/* Body */}
            <div>
              <Label className="font-semibold">Cuerpo del email</Label>
              <RichTextEditor
                value={emailComposer.cuerpo}
                onChange={(html) => setEmailComposer(prev => ({ ...prev, cuerpo: html }))}
                placeholder="Escriba el contenido del email al cliente..."
              />
            </div>

            {/* Actions */}
            <DialogFooter className="flex gap-2 border-t pt-4 sticky bottom-0 bg-white -mx-6 px-6 -mb-6 pb-4 z-10">
              <Button
                variant="outline"
                onClick={handleEmailSaveLater}
                className="mr-auto"
                data-testid="email-composer-save-later"
              >
                <Save className="h-4 w-4 mr-1" />
                Guardar para después
              </Button>
              <Button
                onClick={handleEmailSend}
                disabled={emailSending || !emailComposer.destinatario_email}
                className="bg-purple-600 hover:bg-purple-700"
                data-testid="email-composer-send"
              >
                {emailSending ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : <Send className="h-4 w-4 mr-1" />}
                Enviar Email
              </Button>
            </DialogFooter>
          </div>
        </DialogContent>
      </Dialog>

      {/* Post-confirmation step dialog: Email Sentencia + Datos Faltantes */}
      <Dialog open={postConfirmStep !== 'idle'} onOpenChange={(v) => { if (!v) { setPostConfirmStep('idle'); } }}>
        <DialogContent className="max-h-[92vh] overflow-y-auto"
                       style={{ maxWidth: '1400px', width: '92vw' }}
                       data-testid="post-confirm-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Sparkles className="w-5 h-5 text-amber-500" />
              {postConfirmStep === 'email_sentencia' ? (
                (() => {
                  const t = (confirmForm.tipo_tramite || '').toLowerCase();
                  if (t === 'recurso_apelacion_banco') return "Comunicacio de recurs d'apel·lacio al client";
                  if (t === 'senalamiento') return "Comunicacio d'assenyalament judicial al client";
                  return 'Comunicació de sentència al client';
                })()
              ) : 'Datos detectados en el documento'}
            </DialogTitle>
          </DialogHeader>

          {/* STEP: Loading - preparing post-confirm workflow */}
          {postConfirmStep === 'loading' && (
            <div className="flex flex-col items-center justify-center py-16 space-y-4">
              <div className="relative">
                <Loader2 className="w-14 h-14 text-blue-500 animate-spin" />
                <Sparkles className="w-6 h-6 text-amber-400 absolute -top-2 -right-2 animate-pulse" />
              </div>
              <div className="text-center">
                <p className="font-medium text-gray-800 text-lg">Preparant el següent pas...</p>
                <p className="text-sm text-gray-500 mt-2">Estem analitzant les dades del document i l'expedient</p>
              </div>
            </div>
          )}

          {/* STEP: Email Sentencia */}
          {postConfirmStep === 'email_sentencia' && (
            <div className="space-y-4">
              {generatingEmail ? (
                <div className="flex flex-col items-center justify-center py-12 space-y-4">
                  <div className="relative">
                    <Loader2 className="w-12 h-12 text-blue-500 animate-spin" />
                    <Mail className="w-5 h-5 text-blue-400 absolute -top-1 -right-1 animate-pulse" />
                  </div>
                  <div className="text-center">
                    <p className="font-medium text-gray-800">Generant borrador d'email...</p>
                    <p className="text-sm text-gray-500 mt-1">La IA prepara la comunicació de sentència en català</p>
                  </div>
                </div>
              ) : (
                <>
                  {decretoInfo?.bloqueado && (
                    <div className="p-3 bg-red-50 border border-red-300 rounded-lg" data-testid="decreto-bloqueado">
                      <p className="font-semibold text-red-800">
                        <AlertTriangle className="w-4 h-4 inline mr-1" />
                        Costes imposades al nostre client: no es comunica
                      </p>
                      <p className="text-sm text-red-700 mt-1">
                        {decretoInfo.motivo_bloqueo || "El Decret condemna en costes el nostre client, per aixo no s'envia cap email."}
                      </p>
                      <p className="text-sm text-red-700 mt-1">L'actuacio s'ha creat igualment a l'expedient amb el document adjunt.</p>
                    </div>
                  )}

                  {decretoInfo && !decretoInfo.bloqueado && (
                    <div className="p-3 bg-indigo-50 border border-indigo-200 rounded-lg space-y-2" data-testid="decreto-panel">
                      <div className="flex flex-wrap items-center gap-2 text-sm">
                        <span className="font-medium text-indigo-900">Decret detectat:</span>
                        {decretoInfo.importe_costas != null && (
                          <span className="px-2 py-0.5 rounded bg-white border border-indigo-200" data-testid="decreto-importe-costas">
                            Costes: {fmtImportDecreto(decretoInfo.importe_costas)} €
                          </span>
                        )}
                        {decretoInfo.importe_intereses != null && (
                          <span className="px-2 py-0.5 rounded bg-white border border-indigo-200" data-testid="decreto-importe-intereses">
                            Interessos: {fmtImportDecreto(decretoInfo.importe_intereses)} €
                          </span>
                        )}
                        {decretoInfo.deudor && (
                          <span className="px-2 py-0.5 rounded bg-white border border-indigo-200" data-testid="decreto-deudor">
                            Ha de pagar: {decretoInfo.deudor}
                          </span>
                        )}
                      </div>
                      <div className="flex items-center gap-2">
                        <span className="text-sm text-indigo-900">Idioma:</span>
                        <Button size="sm" variant={decretoIdioma === 'ca' ? 'default' : 'outline'} className="h-7"
                                disabled={cambiandoIdioma} onClick={() => handleCambiarIdiomaDecreto('ca')}
                                data-testid="decreto-idioma-ca">Catala</Button>
                        <Button size="sm" variant={decretoIdioma === 'es' ? 'default' : 'outline'} className="h-7"
                                disabled={cambiandoIdioma} onClick={() => handleCambiarIdiomaDecreto('es')}
                                data-testid="decreto-idioma-es">Castella</Button>
                        {cambiandoIdioma && <Loader2 className="w-4 h-4 animate-spin text-indigo-600" />}
                      </div>
                    </div>
                  )}

                  <div className="flex items-center gap-3 p-3 bg-blue-50 rounded-lg border border-blue-200">
                    <Mail className="w-5 h-5 text-blue-600 flex-shrink-0" />
                    <div>
                      <p className="font-medium text-blue-800">Comunicació de sentència al client</p>
                      <p className="text-sm text-blue-600">La IA ha preparat un borrador d'email. Reviseu-lo i editeu-lo abans d'enviar.</p>
                    </div>
                  </div>

                  {(!emailSentenciaData || !emailSentenciaData.success) && !emailSentenciaDestinatario && (
                    <div className="p-3 bg-amber-50 border border-amber-200 rounded-lg">
                      <p className="text-sm text-amber-700">
                        <AlertTriangle className="w-4 h-4 inline mr-1" />
                        {emailSentenciaData?.message || "No s'ha pogut generar l'email automàticament. Podeu ometre aquest pas."}
                      </p>
                    </div>
                  )}

                  {/* PDF attachment indicator */}
                  {postConfirmAdjuntosRef.current?.emailAdjuntos?.length > 0 && (
                    <div className="flex items-center gap-2 p-2.5 bg-blue-50/80 rounded-lg border border-blue-100">
                      <Paperclip className="w-4 h-4 text-blue-600 flex-shrink-0" />
                      <span className="text-sm text-blue-700">
                        S'adjuntarà automàticament: <strong>{postConfirmAdjuntosRef.current.emailAdjuntos[0]?.nombre || 'document.pdf'}</strong>
                      </span>
                    </div>
                  )}

                  {(emailSentenciaDestinatario || emailSentenciaData?.success) && (
                    <>
                      <div>
                        <Label>Destinatari</Label>
                        <Input
                          data-testid="email-sentencia-destinatario"
                          value={emailSentenciaDestinatario}
                          onChange={(e) => setEmailSentenciaDestinatario(e.target.value)}
                          placeholder="email@client.com"
                          type="email"
                        />
                        {emailSentenciaData?.nombre_cliente && (
                          <p className="text-xs text-gray-500 mt-1">Client: {emailSentenciaData.nombre_cliente}</p>
                        )}
                      </div>
                      <div>
                        <Label>Assumpte</Label>
                        <Input
                          data-testid="email-sentencia-asunto"
                          value={emailSentenciaAsunto}
                          onChange={(e) => setEmailSentenciaAsunto(e.target.value)}
                          placeholder="Assumpte de l'email"
                        />
                      </div>
                      <div>
                        <Label>Cos de l'email</Label>
                        <RichTextEditor
                          value={emailSentenciaCuerpo}
                          onChange={(html) => setEmailSentenciaCuerpo(html)}
                          placeholder="Contingut de l'email..."
                        />
                      </div>
                    </>
                  )}

                  <DialogFooter className="gap-2 sticky bottom-0 bg-white border-t pt-3 -mx-6 px-6 -mb-6 pb-4 z-10">
                    <Button variant="outline" onClick={handleSkipEmailSentencia} data-testid="email-sentencia-skip-btn">
                      <SkipForward className="w-4 h-4 mr-2" />
                      Ometre
                    </Button>
                    {(emailSentenciaDestinatario || emailSentenciaData?.success) && (
                      <Button
                        className="bg-blue-600 hover:bg-blue-700 text-white"
                        disabled={sendingEmailSentencia || !emailSentenciaDestinatario || !emailSentenciaCuerpo.trim()}
                        onClick={handleSendEmailSentencia}
                        data-testid="email-sentencia-send-btn"
                      >
                        {sendingEmailSentencia ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Send className="w-4 h-4 mr-2" />}
                        Enviar al client
                      </Button>
                    )}
                  </DialogFooter>
                </>
              )}
            </div>
          )}

          {/* STEP: Datos Faltantes */}
          {postConfirmStep === 'datos_faltantes' && datosFaltantes.length > 0 && (
            <div className="space-y-4">
              <div className="flex items-center gap-3 p-3 bg-amber-50 rounded-lg border border-amber-200">
                <AlertTriangle className="w-5 h-5 text-amber-600 flex-shrink-0" />
                <div>
                  <p className="font-medium text-amber-800">Datos detectados en el documento</p>
                  <p className="text-sm text-amber-600">La IA ha detectado datos que no constan en el expediente. Selecciona los que quieras incorporar.</p>
                </div>
              </div>

              <div className="space-y-2">
                {datosFaltantes.map((dato, idx) => (
                  <label
                    key={idx}
                    className={`flex items-start gap-3 p-3 rounded-lg border cursor-pointer transition-colors ${
                      datosSeleccionados[idx]
                        ? 'bg-blue-50 border-blue-300'
                        : 'bg-gray-50 border-gray-200 opacity-60'
                    }`}
                    data-testid={`dato-faltante-${dato.campo}`}
                  >
                    <input
                      type="checkbox"
                      className="mt-1 w-4 h-4 rounded"
                      checked={!!datosSeleccionados[idx]}
                      onChange={(e) => setDatosSeleccionados(prev => ({ ...prev, [idx]: e.target.checked }))}
                    />
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        <Badge variant="secondary" className="text-xs shrink-0">
                          {dato.tipo === 'participante' ? 'Participante' : 'Expediente'}
                        </Badge>
                        <span className="font-medium text-sm">{dato.etiqueta}</span>
                      </div>
                      <p className="text-sm text-gray-700 mt-1 break-words">{dato.valor_detectado}</p>
                      {dato.contacto_id_existente ? (
                        <p className="text-xs text-green-700 mt-0.5 font-medium">
                          Coincide con registro existente: "{dato.nombre_existente}" - se vinculará al expediente
                        </p>
                      ) : dato.tipo === 'participante' ? (
                        <p className="text-xs text-gray-500 mt-0.5">
                          No se encontró en contactos existentes. Se creará automáticamente.
                        </p>
                      ) : dato.campo === 'juzgado' ? (
                        <p className="text-xs text-gray-500 mt-0.5">
                          No se encontró en organismos existentes. Se creará automáticamente.
                        </p>
                      ) : null}
                    </div>
                  </label>
                ))}
              </div>

              <DialogFooter className="gap-2">
                <Button variant="outline" onClick={handleSkipDatos} data-testid="ia-skip-datos-btn">
                  Omitir
                </Button>
                <Button
                  className="bg-blue-600 hover:bg-blue-700 text-white"
                  disabled={actualizandoDatos || !Object.values(datosSeleccionados).some(v => v)}
                  onClick={handleIncorporarDatos}
                  data-testid="ia-incorporar-datos-btn"
                >
                  {actualizandoDatos ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Check className="w-4 h-4 mr-2" />}
                  Incorporar datos al expediente
                </Button>
              </DialogFooter>
            </div>
          )}
        </DialogContent>
      </Dialog>
      {/* Diálogo confirmación descarte */}
      <Dialog open={discardConfirm !== null} onOpenChange={(open) => { if (!open) setDiscardConfirm(null); }}>
        <DialogContent className="max-w-md" data-testid="discard-confirm-dialog">
          <div className="space-y-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-full bg-red-100 flex items-center justify-center">
                <Trash2 className="h-5 w-5 text-red-600" />
              </div>
              <div>
                <h3 className="font-semibold text-gray-900">Descartar email</h3>
                <p className="text-sm text-gray-500">Esta acción moverá el email a descartados</p>
              </div>
            </div>
            <p className="text-sm text-gray-600">
              ¿Estás seguro de que quieres descartar este email? Podrás verlo en la pestaña de descartados.
            </p>
            <DialogFooter className="gap-2">
              <Button variant="outline" onClick={() => setDiscardConfirm(null)} data-testid="discard-cancel-btn">
                Cancelar
              </Button>
              <Button
                variant="destructive"
                onClick={async () => {
                  const emailId = discardConfirm;
                  setDiscardConfirm(null);
                  await handleDiscard(emailId);
                }}
                data-testid="discard-confirm-btn"
              >
                <Trash2 className="h-4 w-4 mr-1" /> Descartar
              </Button>
            </DialogFooter>
          </div>
        </DialogContent>
      </Dialog>
      </>
      )}

      {/* Diálogo estimación coste lote IA */}
      <DialogEstimacionLote
        open={estimacionLoteOpen}
        onClose={() => setEstimacionLoteOpen(false)}
        onConfirm={handleProcessAll}
        cantidad={selectedEmails.size > 0 ? selectedEmails.size : (stats?.descargados_sin_procesar || 0)}
        modelo={iaConfigActual?.modelo || 'claude-sonnet-4-5-20250929'}
        proveedor={iaConfigActual?.proveedor || 'anthropic'}
        titulo={`Analizar ${selectedEmails.size > 0 ? selectedEmails.size : (stats?.descargados_sin_procesar || 0)} emails con IA`}
      />

      <DialogEscritoIA open={escritoIADialog.open} email={escritoIADialog.email} onClose={() => setEscritoIADialog({ open: false, email: null })} onSaved={() => { try { loadEmails && loadEmails(); } catch(e){} }} />
    </div>
  );
}

// Sub-component: Historial tab
function HistorialTab({ formatDate }) {
  const [emails, setEmails] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    loadHistorial();
  }, []);

  const loadHistorial = async () => {
    setLoading(true);
    try {
      const res = await automatizacionAPI.listEmails({ limit: 200 });
      setEmails(res.data.emails || []);
    } catch (err) {
      console.error('Error cargando historial:', err);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-16">
        <Loader2 className="h-8 w-8 animate-spin text-gray-400" />
      </div>
    );
  }

  return (
    <div className="overflow-x-auto">
      <table className="w-full text-sm" data-testid="historial-table">
        <thead>
          <tr className="border-b bg-gray-50">
            <th className="text-left p-2">Fecha</th>
            <th className="text-left p-2">Remitente</th>
            <th className="text-left p-2">Asunto</th>
            <th className="text-left p-2">Expediente</th>
            <th className="text-left p-2">Estado</th>
            <th className="text-left p-2">Actuación</th>
          </tr>
        </thead>
        <tbody>
          {emails.map((em) => {
            const estadoInfo = ESTADOS[em.estado] || ESTADOS.nuevo;
            return (
              <tr key={em.id} className="border-b hover:bg-gray-50">
                <td className="p-2 text-xs whitespace-nowrap">{formatDate(em.fecha_email)}</td>
                <td className="p-2 text-xs">{em.remitente_nombre || em.remitente}</td>
                <td className="p-2 text-xs max-w-[200px] truncate">{em.asunto}</td>
                <td className="p-2 text-xs">
                  {em.ia_expediente_numero && (
                    <Badge variant="outline" className="text-xs">{em.ia_expediente_numero}</Badge>
                  )}
                </td>
                <td className="p-2">
                  <Badge className={`${estadoInfo.color} text-xs`}>{estadoInfo.label}</Badge>
                </td>
                <td className="p-2 text-xs">
                  {em.actuacion_id ? (
                    <span className="text-green-600">#{em.actuacion_id}</span>
                  ) : '-'}
                </td>
              </tr>
            );
          })}
          {emails.length === 0 && (
            <tr>
              <td colSpan={6} className="p-8 text-center text-gray-500">No hay registros</td>
            </tr>
          )}
        </tbody>
      </table>
    </div>
  );
}


// Sub-component: Reglas tab
function ReglasTab() {
  const [reglas, setReglas] = useState([]);
  const [loading, setLoading] = useState(false);
  const [editDialog, setEditDialog] = useState({ open: false, regla: null });
  const [form, setForm] = useState({
    nombre: '', activa: true, prioridad: 10,
    condicion_remitente: '', condicion_asunto_contiene: '', condicion_cuerpo_contiene: '',
    accion_tipo_tramite: '', accion_titulo_prefijo: ''
  });
  const [saving, setSaving] = useState(false);

  useEffect(() => { loadReglas(); }, []);

  const loadReglas = async () => {
    setLoading(true);
    try {
      const res = await automatizacionAPI.listReglas();
      setReglas(res.data.reglas || []);
    } catch (err) {
      console.error('Error:', err);
    } finally {
      setLoading(false);
    }
  };

  const openNew = () => {
    setForm({
      nombre: '', activa: true, prioridad: 10,
      condicion_remitente: '', condicion_asunto_contiene: '', condicion_cuerpo_contiene: '',
      accion_tipo_tramite: '', accion_titulo_prefijo: ''
    });
    setEditDialog({ open: true, regla: null });
  };

  const openEdit = (r) => {
    setForm({
      nombre: r.nombre || '', activa: r.activa !== false, prioridad: r.prioridad || 10,
      condicion_remitente: r.condicion_remitente || '',
      condicion_asunto_contiene: r.condicion_asunto_contiene || '',
      condicion_cuerpo_contiene: r.condicion_cuerpo_contiene || '',
      accion_tipo_tramite: r.accion_tipo_tramite || '',
      accion_titulo_prefijo: r.accion_titulo_prefijo || ''
    });
    setEditDialog({ open: true, regla: r });
  };

  const handleSave = async () => {
    if (!form.nombre) { toast({ title: 'Nombre obligatorio', variant: 'destructive' }); return; }
    setSaving(true);
    try {
      if (editDialog.regla) {
        await automatizacionAPI.updateRegla(editDialog.regla.id, form);
        toast({ title: 'Regla actualizada' });
      } else {
        await automatizacionAPI.createRegla(form);
        toast({ title: 'Regla creada' });
      }
      setEditDialog({ open: false, regla: null });
      loadReglas();
    } catch (err) {
      toast({ title: 'Error', variant: 'destructive' });
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('¿Eliminar esta regla?')) return;
    try {
      await automatizacionAPI.deleteRegla(id);
      toast({ title: 'Regla eliminada' });
      loadReglas();
    } catch (err) {
      toast({ title: 'Error', variant: 'destructive' });
    }
  };

  const TIPOS = [
    { value: 'notificacion', label: 'Notificación' },
    { value: 'escrito', label: 'Escrito' },
    { value: 'respuesta', label: 'Respuesta' },
    { value: 'tramite', label: 'Trámite' },
    { value: 'providencia', label: 'Providencia' },
    { value: 'sentencia', label: 'Sentencia' },
    { value: 'auto', label: 'Auto' },
    { value: 'emplazamiento', label: 'Emplazamiento' },
    { value: 'recurso_apelacion_banco', label: "Recurs apel·lació (part contraria)" },
    { value: 'senalamiento', label: "Senyalament (vista/audiència/judici)" },
  ];

  if (loading) return <div className="flex justify-center py-8"><Loader2 className="h-8 w-8 animate-spin text-gray-400" /></div>;

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div>
          <h3 className="font-medium text-gray-900">Reglas automáticas de clasificación</h3>
          <p className="text-xs text-gray-500">Defina reglas para pre-clasificar emails según remitente, asunto o contenido</p>
        </div>
        <Button size="sm" onClick={openNew} data-testid="new-regla-btn">
          <Plus className="h-4 w-4 mr-1" /> Nueva Regla
        </Button>
      </div>

      {reglas.length === 0 ? (
        <Card>
          <CardContent className="flex flex-col items-center justify-center py-12 text-gray-500">
            <List className="h-10 w-10 mb-2 text-gray-300" />
            <p className="font-medium">No hay reglas definidas</p>
            <p className="text-sm mt-1">Las reglas permiten automatizar la clasificación de emails</p>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-2">
          {reglas.map((r) => (
            <Card key={r.id} className={`${r.activa ? '' : 'opacity-50'}`} data-testid={`regla-card-${r.id}`}>
              <CardContent className="p-3">
                <div className="flex items-center justify-between">
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-1">
                      <span className="font-medium text-sm">{r.nombre}</span>
                      <Badge className={r.activa ? 'bg-green-100 text-green-700 text-xs' : 'bg-gray-100 text-gray-500 text-xs'}>
                        {r.activa ? 'Activa' : 'Inactiva'}
                      </Badge>
                      <span className="text-xs text-gray-400">Prioridad: {r.prioridad}</span>
                    </div>
                    <div className="flex flex-wrap gap-2 text-xs">
                      {r.condicion_remitente && (
                        <span className="bg-blue-50 text-blue-700 px-2 py-0.5 rounded">
                          Remitente contiene: <strong>{r.condicion_remitente}</strong>
                        </span>
                      )}
                      {r.condicion_asunto_contiene && (
                        <span className="bg-purple-50 text-purple-700 px-2 py-0.5 rounded">
                          Asunto contiene: <strong>{r.condicion_asunto_contiene}</strong>
                        </span>
                      )}
                      {r.condicion_cuerpo_contiene && (
                        <span className="bg-orange-50 text-orange-700 px-2 py-0.5 rounded">
                          Cuerpo contiene: <strong>{r.condicion_cuerpo_contiene}</strong>
                        </span>
                      )}
                      {r.accion_tipo_tramite && (
                        <span className="bg-green-50 text-green-700 px-2 py-0.5 rounded">
                          Tipo: <strong>{r.accion_tipo_tramite}</strong>
                        </span>
                      )}
                      {r.accion_titulo_prefijo && (
                        <span className="bg-amber-50 text-amber-700 px-2 py-0.5 rounded">
                          Prefijo: <strong>{r.accion_titulo_prefijo}</strong>
                        </span>
                      )}
                    </div>
                  </div>
                  <div className="flex items-center gap-1 ml-2">
                    <Button variant="ghost" size="sm" onClick={() => openEdit(r)}>
                      <Edit2 className="h-4 w-4" />
                    </Button>
                    <Button variant="ghost" size="sm" className="text-red-500" onClick={() => handleDelete(r.id)}>
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* Regla Edit Dialog */}
      <Dialog open={editDialog.open} onOpenChange={(v) => !v && setEditDialog({ open: false, regla: null })}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>{editDialog.regla ? 'Editar Regla' : 'Nueva Regla'}</DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <Label>Nombre de la regla *</Label>
              <Input value={form.nombre} onChange={(e) => setForm({...form, nombre: e.target.value})} placeholder="Ej: Sentencias de juzgados" />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="flex items-center gap-2">
                <input type="checkbox" id="regla_activa" checked={form.activa} onChange={(e) => setForm({...form, activa: e.target.checked})} />
                <Label htmlFor="regla_activa">Activa</Label>
              </div>
              <div>
                <Label>Prioridad (menor = primero)</Label>
                <Input type="number" value={form.prioridad} onChange={(e) => setForm({...form, prioridad: parseInt(e.target.value) || 10})} min={1} />
              </div>
            </div>

            <div className="border-t pt-3">
              <h4 className="text-sm font-medium mb-2">Condiciones (se combinan con AND)</h4>
              <div className="space-y-2">
                <div>
                  <Label>Remitente contiene</Label>
                  <Input value={form.condicion_remitente} onChange={(e) => setForm({...form, condicion_remitente: e.target.value})} placeholder="ej: @juzgado.es" />
                </div>
                <div>
                  <Label>Asunto contiene</Label>
                  <Input value={form.condicion_asunto_contiene} onChange={(e) => setForm({...form, condicion_asunto_contiene: e.target.value})} placeholder="ej: sentencia" />
                </div>
                <div>
                  <Label>Cuerpo contiene</Label>
                  <Input value={form.condicion_cuerpo_contiene} onChange={(e) => setForm({...form, condicion_cuerpo_contiene: e.target.value})} placeholder="ej: fallo" />
                </div>
              </div>
            </div>

            <div className="border-t pt-3">
              <h4 className="text-sm font-medium mb-2">Acciones (sobreescriben la propuesta IA)</h4>
              <div className="space-y-2">
                <div>
                  <Label>Asignar tipo de trámite</Label>
                  <select
                    className="w-full border rounded-md px-3 py-2 text-sm"
                    value={form.accion_tipo_tramite}
                    onChange={(e) => setForm({...form, accion_tipo_tramite: e.target.value})}
                  >
                    <option value="">Sin cambio</option>
                    {TIPOS.map(t => <option key={t.value} value={t.value}>{t.label}</option>)}
                  </select>
                </div>
                <div>
                  <Label>Prefijo del título</Label>
                  <Input value={form.accion_titulo_prefijo} onChange={(e) => setForm({...form, accion_titulo_prefijo: e.target.value})} placeholder="ej: SENTENCIA" />
                </div>
              </div>
            </div>

            <DialogFooter>
              <Button variant="outline" onClick={() => setEditDialog({ open: false, regla: null })}>Cancelar</Button>
              <Button onClick={handleSave} disabled={saving}>
                {saving ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : null}
                {editDialog.regla ? 'Guardar' : 'Crear'}
              </Button>
            </DialogFooter>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
